/* eslint-disable react/no-unknown-property */
import { Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Stars, Text, Html, Billboard, Sky as DreiSky, Cloud, Clouds } from "@react-three/drei";
import * as THREE from "three";
import { AIRPORTS } from "@/data/sections";
import { useWorldStore } from "@/components/world/store";
import { prewarmSpaceTextures } from "@/components/world/textures";
import { AirlinerMesh } from "@/components/world/models/Airliner";
import { SpaceshipMesh } from "@/components/world/models/Spaceship";
import { TinyPerson, TinyDog } from "@/components/world/models/Characters";
import { Flagpole } from "@/components/world/models/Flag";
import { ControlTower, Terminal, House, Tree } from "@/components/world/models/Buildings";
import { EiffelTower, HongKongTower, StatueOfLiberty } from "@/components/world/models/Monuments";
import { Planet, EarthPlanet } from "@/components/world/models/Planets";
import { Satellite, ISS, AlienShip, Asteroid } from "@/components/world/models/SpaceObjects";

/* ============================================================
   Reusable geometry shapes (triangular sails, etc.)
   ============================================================ */
const MAIN_SAIL_SHAPE = (() => {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.lineTo(0, 6.8);   // luff (up the mast)
  s.lineTo(4.2, 0.1); // leech (from head down to clew)
  s.lineTo(0, 0);
  return s;
})();
const JIB_SAIL_SHAPE = (() => {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.lineTo(0, 5.2);
  s.lineTo(2.6, 0);
  s.lineTo(0, 0);
  return s;
})();
/* Curved (belly) sail via extruded shape — gives a wind-filled look */
const CURVED_MAIN_SAIL = (() => {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.quadraticCurveTo(-0.6, 3.4, 0, 6.8);
  s.quadraticCurveTo(2.2, 3.6, 4.2, 0.1);
  s.lineTo(0, 0);
  return s;
})();
const CURVED_JIB_SAIL = (() => {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.quadraticCurveTo(-0.4, 2.6, 0, 5.2);
  s.quadraticCurveTo(1.4, 2.8, 2.6, 0);
  s.lineTo(0, 0);
  return s;
})();

/* ============================================================
   Airplane / Spaceship - hybrid: same group, but geometry swaps
   with spaceMode. Handles flight physics + chase camera + escape
   ============================================================ */
function Airplane() {
  const group = useRef();
  const propRef = useRef();
  const leftTrailRefs = useRef([]);
  const rightTrailRefs = useRef([]);
  const leftTrail = useRef([]);
  const rightTrail = useRef([]);
  const TRAIL_LEN = 26;
  const yaw = useRef(0);
  const pitch = useRef(0);
  const rollVisual = useRef(0);
  const velocity = useRef(new THREE.Vector3());
  const spaceEnteredAt = useRef(0);
  const returnCooldown = useRef(0);

  const openId = useWorldStore((s) => s.openId);
  const input = useWorldStore((s) => s.input);
  const spaceMode = useWorldStore((s) => s.spaceMode);
  const underwaterMode = useWorldStore((s) => s.underwaterMode);
  const escapeCharge = useWorldStore((s) => s.escapeCharge);
  const setNearest = useWorldStore((s) => s.setNearest);
  const setTelemetry = useWorldStore((s) => s.setTelemetry);
  const setEscapeCharge = useWorldStore((s) => s.setEscapeCharge);
  const setSpaceMode = useWorldStore((s) => s.setSpaceMode);
  const setUnderwaterMode = useWorldStore((s) => s.setUnderwaterMode);
  const setTransition = useWorldStore((s) => s.setTransition);

  const tmp = useMemo(() => new THREE.Vector3(), []);
  const tmp2 = useMemo(() => new THREE.Vector3(), []);
  const camTarget = useMemo(() => new THREE.Vector3(), []);

  useFrame((state, delta) => {
    if (!group.current) return;
    // Cap delta to prevent physics tunneling when tab was inactive.
    // Without this, coming back from a long tab-switch would teleport the
    // vehicle into infinity because delta could be several seconds.
    delta = Math.min(delta, 0.033);
    const paused = !!openId;
    const st = useWorldStore.getState();

    // ----- Input (keyboard mirrored to store) -----
    if (!paused) {
      const turnRate = spaceMode ? 0.9 : 1.1;
      const pitchRate = spaceMode ? 0.75 : 0.9;
      if (input.left) yaw.current += delta * turnRate;
      if (input.right) yaw.current -= delta * turnRate;

      // INVERTED (real airplane): UP arrow = push stick forward = DIVE (pitch negative → nose down)
      // DOWN arrow = pull stick back = CLIMB (pitch positive → nose up)
      if (input.up) pitch.current = Math.max(pitch.current - delta * pitchRate, -0.55);
      else if (input.down) pitch.current = Math.min(pitch.current + delta * pitchRate, 0.55);
      else pitch.current *= 1 - Math.min(delta * 2, 1);

      // Roll visually when banking
      const targetRoll = (input.left ? 0.55 : 0) + (input.right ? -0.55 : 0);
      rollVisual.current += (targetRoll - rollVisual.current) * Math.min(delta * 4, 1);

      // Speed
      const baseSpeed = spaceMode ? 24 : 14;
      const boostSpeed = spaceMode ? 55 : 32;
      const speed = input.boost ? boostSpeed : baseSpeed;

      // Forward direction: pitch positive → climb (nose up → y velocity positive)
      const forward = tmp.set(
        -Math.sin(yaw.current) * Math.cos(pitch.current),
        Math.sin(pitch.current),
        -Math.cos(yaw.current) * Math.cos(pitch.current)
      );

      velocity.current.copy(forward).multiplyScalar(speed);
      group.current.position.addScaledVector(velocity.current, delta);

      // ---- Altitude clamp (allow diving underwater in Earth mode) ----
      if (spaceMode) {
        group.current.position.y = THREE.MathUtils.clamp(group.current.position.y, -60, 220);
      } else if (underwaterMode) {
        // Underwater: seafloor is at -30, keep sub above it
        group.current.position.y = THREE.MathUtils.clamp(group.current.position.y, -27, 110);
      } else {
        group.current.position.y = THREE.MathUtils.clamp(group.current.position.y, -50, 110);
      }

      // ---- Island vertical collision (only when trying to dive DOWN through them) ----
      // You CAN fly over islands. You CAN'T descend below their surface to reach underwater.
      if (!spaceMode && !underwaterMode) {
        let overIsland = false;
        for (const ap of AIRPORTS) {
          const dx = group.current.position.x - ap.position[0];
          const dz = group.current.position.z - ap.position[2];
          if (dx * dx + dz * dz < 44 * 44) { overIsland = true; break; }
        }
        // When over an island, floor is +2 (grass level). Otherwise dive freely to -50.
        const minY = overIsland ? 2 : -50;
        if (group.current.position.y < minY) group.current.position.y = minY;
      }
      if (!spaceMode) {
        if (!underwaterMode && group.current.position.y < -1.5) {
          setUnderwaterMode(true);
        } else if (underwaterMode && group.current.position.y > 3) {
          setUnderwaterMode(false);
        }
      }

      // Soft world boundary
      const BOUND = spaceMode ? 380 : 280;
      const cx = group.current.position.x;
      const cz = group.current.position.z;
      const dFromCenter = Math.sqrt(cx * cx + cz * cz);
      if (dFromCenter > BOUND) {
        const pushK = Math.min(1, (dFromCenter - BOUND) / 40);
        group.current.position.x -= (cx / dFromCenter) * pushK * delta * 40;
        group.current.position.z -= (cz / dFromCenter) * pushK * delta * 40;
      }

      // ---- Escape charge logic (Earth mode only) ----
      if (!spaceMode && st.transitionPhase === "idle") {
        const altitude = group.current.position.y;
        // Climbing when pitch is positive (nose up)
        const climbing = pitch.current > 0.15;
        const currentCharge = useWorldStore.getState().escapeCharge;
        if (climbing && input.boost && altitude > 55) {
          const newCharge = Math.min(1, currentCharge + delta * 0.35);
          setEscapeCharge(newCharge);
          if (newCharge >= 1) {
            spaceEnteredAt.current = state.clock.elapsedTime;
            setTransition("ascending", 0);
            setEscapeCharge(0);
          }
        } else if (currentCharge > 0.0001) {
          setEscapeCharge(Math.max(0, currentCharge - delta * 0.95));
        }
      }

      // ---- Transition ascending: rush up into space ----
      if (st.transitionPhase === "ascending") {
        if (spaceEnteredAt.current === 0) spaceEnteredAt.current = state.clock.elapsedTime;
        const elapsed = state.clock.elapsedTime - spaceEnteredAt.current;
        const dur = 1.8;
        const prog = Math.min(1, elapsed / dur);
        setTransition("ascending", prog);
        // Force nose up (positive pitch)
        pitch.current = THREE.MathUtils.lerp(pitch.current, 0.5, delta * 3);
        group.current.position.y += delta * 55 * prog;
        if (prog >= 1) {
          setSpaceMode(true);
          setTransition("idle", 0);
          spaceEnteredAt.current = 0;
          pitch.current = 0.1;
        }
      }

      // ---- Transition descending: return to earth ----
      if (st.transitionPhase === "descending") {
        if (spaceEnteredAt.current === 0) spaceEnteredAt.current = state.clock.elapsedTime;
        const elapsed = state.clock.elapsedTime - spaceEnteredAt.current;
        const dur = 1.8;
        const prog = Math.min(1, elapsed / dur);
        setTransition("descending", prog);
        // Force nose down (negative pitch)
        pitch.current = THREE.MathUtils.lerp(pitch.current, -0.3, delta * 3);
        if (prog >= 1) {
          setSpaceMode(false);
          setTransition("idle", 0);
          spaceEnteredAt.current = 0;
          returnCooldown.current = state.clock.elapsedTime + 1.5;
          group.current.position.y = THREE.MathUtils.clamp(group.current.position.y, 30, 90);
          pitch.current = -0.1;
        }
      }
    }

    // Apply rotation with YXZ order (aviation standard: yaw → pitch → roll relative to plane)
    group.current.rotation.set(pitch.current, yaw.current, rollVisual.current, "YXZ");

    // Spin prop
    if (propRef.current) propRef.current.rotation.z += delta * (paused ? 4 : 40);

    // ----- Contrail: emit from EACH engine world position -----
    if (!paused) {
      const boostMultiplier = input.boost ? 1 : 0.55;
      // Engine offsets in plane-local coordinates (scale 0.75 already applied)
      // Engines at local [-3.5, -0.75, 0.4] × 0.75 = [-2.625, -0.5625, 0.3], trail emits slightly behind
      const eul = new THREE.Euler(pitch.current, yaw.current, rollVisual.current);
      const engineL_local = tmp2.set(-2.625, -0.5625, 1.5).applyEuler(eul);
      const engineR_local = tmp.set(2.625, -0.5625, 1.5).applyEuler(eul);
      leftTrail.current.unshift({
        x: group.current.position.x + engineL_local.x,
        y: group.current.position.y + engineL_local.y,
        z: group.current.position.z + engineL_local.z,
      });
      rightTrail.current.unshift({
        x: group.current.position.x + engineR_local.x,
        y: group.current.position.y + engineR_local.y,
        z: group.current.position.z + engineR_local.z,
      });
      if (leftTrail.current.length > TRAIL_LEN) leftTrail.current.length = TRAIL_LEN;
      if (rightTrail.current.length > TRAIL_LEN) rightTrail.current.length = TRAIL_LEN;

      const trailColor = spaceMode ? "#9AC5FF" : "#F4EADE";
      for (let i = 0; i < TRAIL_LEN; i++) {
        const refL = leftTrailRefs.current[i];
        const refR = rightTrailRefs.current[i];
        const pL = leftTrail.current[i];
        const pR = rightTrail.current[i];
        const life = 1 - i / TRAIL_LEN;
        const scale = (0.35 + i * 0.11) * boostMultiplier;
        if (refL) {
          if (pL) {
            refL.visible = true;
            refL.position.set(pL.x, pL.y, pL.z);
            refL.scale.setScalar(scale);
            if (refL.material) {
              refL.material.opacity = life * 0.55 * boostMultiplier;
              refL.material.color.set(trailColor);
            }
          } else {
            refL.visible = false;
          }
        }
        if (refR) {
          if (pR) {
            refR.visible = true;
            refR.position.set(pR.x, pR.y, pR.z);
            refR.scale.setScalar(scale);
            if (refR.material) {
              refR.material.opacity = life * 0.55 * boostMultiplier;
              refR.material.color.set(trailColor);
            }
          } else {
            refR.visible = false;
          }
        }
      }
    }

    // ----- Camera chase -----
    const behind = tmp.set(0, 6, 22).applyEuler(new THREE.Euler(pitch.current * 0.3, yaw.current, 0));
    camTarget.copy(group.current.position).add(behind);
    state.camera.position.lerp(camTarget, Math.min(delta * 3.5, 1));

    const lookAtOffset = tmp2.set(0, 1.5, -14).applyEuler(new THREE.Euler(pitch.current * 0.3, yaw.current, 0));
    state.camera.lookAt(
      group.current.position.x + lookAtOffset.x,
      group.current.position.y + lookAtOffset.y,
      group.current.position.z + lookAtOffset.z
    );

    // ----- Find nearest airport/planet -----
    let nearestId = null;
    let nearestDist = Infinity;
    for (const ap of AIRPORTS) {
      const dx = group.current.position.x - ap.position[0];
      const dz = group.current.position.z - ap.position[2];
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d < nearestDist) {
        nearestDist = d;
        nearestId = ap.id;
      }
    }
    // In space, also check the Back-to-Earth planet and auto-return on close approach
    if (spaceMode) {
      const dx = group.current.position.x - (-30);
      const dy = group.current.position.y - 20;
      const dz = group.current.position.z - 240;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d < nearestDist) {
        nearestDist = d;
        nearestId = "earth-return";
      }
      // Auto-trigger descending transition when very close (flying INTO the planet)
      if (d < 24 && st.transitionPhase === "idle") {
        setTransition("descending", 0);
      }
    }
    const LAND_RADIUS = spaceMode ? 42 : 34;
    setNearest(nearestDist < LAND_RADIUS ? nearestId : null, nearestDist);

    // Telemetry
    setTelemetry({
      altitude: group.current.position.y,
      speed: velocity.current.length(),
      heading: yaw.current,
      position: {
        x: group.current.position.x,
        y: group.current.position.y,
        z: group.current.position.z,
      },
    });
  });

  return (
    <>
      <group ref={group} position={[
        useWorldStore.getState().position.x,
        useWorldStore.getState().position.y,
        useWorldStore.getState().position.z,
      ]}>
        {spaceMode ? <SpaceshipMesh propRef={propRef} /> : (underwaterMode ? <SubmarineMesh propRef={propRef} /> : <AirlinerMesh propRef={propRef} />)}
        <pointLight color={spaceMode ? "#9AC5FF" : (underwaterMode ? "#F0A020" : "#E94B4B")} intensity={spaceMode ? 1.4 : (underwaterMode ? 1.6 : 0.9)} distance={underwaterMode ? 30 : 18} position={[0, -0.6, 0]} />
      </group>
      {/* Twin contrails - one per engine (world-space) */}
      {[...Array(TRAIL_LEN)].map((_, i) => (
        <mesh key={`l-${i}`} ref={(el) => (leftTrailRefs.current[i] = el)} visible={false}>
          <sphereGeometry args={[0.32, 8, 8]} />
          <meshBasicMaterial color="#F4EADE" transparent opacity={0.5} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
      {[...Array(TRAIL_LEN)].map((_, i) => (
        <mesh key={`r-${i}`} ref={(el) => (rightTrailRefs.current[i] = el)} visible={false}>
          <sphereGeometry args={[0.32, 8, 8]} />
          <meshBasicMaterial color="#F4EADE" transparent opacity={0.5} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </>
  );
}


/* ============================================================
   Submarine mesh - underwater vehicle
   ============================================================ */
function SubmarineMesh({ propRef }) {
  const lightRef = useRef();
  useFrame((state) => {
    if (lightRef.current) {
      lightRef.current.material.emissiveIntensity = 1.6 + Math.sin(state.clock.elapsedTime * 3) * 0.4;
    }
  });
  return (
    <group scale={0.85}>
      {/* Main hull - elongated capsule pointing -Z */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <capsuleGeometry args={[1.1, 5, 8, 20]} />
        <meshStandardMaterial color="#F0A020" metalness={0.5} roughness={0.35} />
      </mesh>
      {/* Belly stripe (darker orange) */}
      <mesh position={[0, -0.4, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[0.95, 1, 0.5]}>
        <capsuleGeometry args={[1.1, 5, 8, 16]} />
        <meshStandardMaterial color="#B08830" metalness={0.5} roughness={0.4} />
      </mesh>
      {/* Conning tower */}
      <mesh position={[0, 1.3, -0.5]}>
        <boxGeometry args={[1.6, 1.2, 2]} />
        <meshStandardMaterial color="#B08830" metalness={0.5} roughness={0.35} />
      </mesh>
      {/* Conning tower window (dark glass) */}
      <mesh position={[0, 1.35, -1.4]} rotation={[-0.2, 0, 0]}>
        <boxGeometry args={[1.4, 0.6, 0.1]} />
        <meshStandardMaterial color="#0F0D11" metalness={0.9} roughness={0.05} emissive="#5A99CC" emissiveIntensity={0.5} />
      </mesh>
      {/* Periscope */}
      <mesh position={[0, 2.4, -0.5]}>
        <cylinderGeometry args={[0.1, 0.1, 1.4, 6]} />
        <meshStandardMaterial color="#B9AFA6" metalness={0.85} roughness={0.15} />
      </mesh>
      <mesh position={[0, 3.05, -0.2]}>
        <boxGeometry args={[0.45, 0.25, 0.65]} />
        <meshStandardMaterial color="#B9AFA6" metalness={0.85} />
      </mesh>
      {/* Portholes (round windows) - 3 per side */}
      {[-1.5, 0, 1.5].map((z, i) => (
        <group key={i}>
          <mesh position={[1.05, 0.2, z]} rotation={[0, Math.PI / 2, 0]}>
            <cylinderGeometry args={[0.28, 0.28, 0.08, 12]} />
            <meshStandardMaterial color="#EAF3FF" emissive="#5A99CC" emissiveIntensity={0.6} metalness={0.6} roughness={0.15} />
          </mesh>
          <mesh position={[-1.05, 0.2, z]} rotation={[0, Math.PI / 2, 0]}>
            <cylinderGeometry args={[0.28, 0.28, 0.08, 12]} />
            <meshStandardMaterial color="#EAF3FF" emissive="#5A99CC" emissiveIntensity={0.6} metalness={0.6} roughness={0.15} />
          </mesh>
        </group>
      ))}
      {/* Propeller cage at rear (+Z) */}
      <mesh position={[0, 0, 3.2]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.65, 0.09, 8, 14]} />
        <meshStandardMaterial color="#3A2A2E" metalness={0.6} />
      </mesh>
      {/* Propeller blades (spinning) */}
      <group ref={propRef} position={[0, 0, 3.35]}>
        {[0, 60, 120, 180, 240, 300].map((deg, i) => (
          <mesh key={i} rotation={[0, 0, (deg * Math.PI) / 180]}>
            <boxGeometry args={[0.5, 0.08, 0.06]} />
            <meshStandardMaterial color="#B9AFA6" metalness={0.9} roughness={0.1} />
          </mesh>
        ))}
      </group>
      {/* Rear vertical fin */}
      <mesh position={[0, 0.9, 2.4]}>
        <boxGeometry args={[0.15, 1.1, 1.6]} />
        <meshStandardMaterial color="#B08830" />
      </mesh>
      {/* Rear horizontal stabilizers */}
      <mesh position={[0, 0, 2.4]}>
        <boxGeometry args={[3, 0.15, 1.6]} />
        <meshStandardMaterial color="#B08830" />
      </mesh>
      {/* Front floodlight (nose beam) */}
      <mesh ref={lightRef} position={[0, 0, -3.4]}>
        <sphereGeometry args={[0.32, 12, 10]} />
        <meshStandardMaterial color="#FFFDD0" emissive="#FFF9C8" emissiveIntensity={1.8} toneMapped={false} />
      </mesh>
      {/* Front light housing */}
      <mesh position={[0, 0, -3.2]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.4, 0.35, 0.4, 12]} />
        <meshStandardMaterial color="#3A2A2E" metalness={0.7} />
      </mesh>
      {/* GREINCH livery */}
      <mesh position={[1.05, 0.7, -0.3]}>
        <planeGeometry args={[0.02, 0.4]} />
        <meshStandardMaterial color="#E94B4B" emissive="#E94B4B" emissiveIntensity={0.5} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}


/* ============================================================
   Rotating beacon
   ============================================================ */
function RotatingBeacon({ color, beaconRef, active }) {
  const sweepRef = useRef();
  const light1 = useRef();
  const light2 = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const rot = t * 1.6;
    if (sweepRef.current) sweepRef.current.rotation.y = rot;
    if (light1.current) {
      light1.current.position.x = Math.cos(rot) * 3.5;
      light1.current.position.z = Math.sin(rot) * 3.5;
    }
    if (light2.current) {
      light2.current.position.x = Math.cos(rot + Math.PI) * 3.5;
      light2.current.position.z = Math.sin(rot + Math.PI) * 3.5;
    }
  });
  return (
    <group position={[0, 6.4, 0]}>
      <mesh ref={beaconRef}>
        <sphereGeometry args={[0.35, 12, 12]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={1.6} toneMapped={false} />
      </mesh>
      <group ref={sweepRef}>
        <mesh position={[3, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
          <coneGeometry args={[0.55, 6, 12, 1, true]} />
          <meshBasicMaterial color={color} transparent opacity={0.35} depthWrite={false} toneMapped={false} />
        </mesh>
        <mesh position={[-3, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <coneGeometry args={[0.55, 6, 12, 1, true]} />
          <meshBasicMaterial color={color} transparent opacity={0.35} depthWrite={false} toneMapped={false} />
        </mesh>
      </group>
      <mesh ref={light1}>
        <sphereGeometry args={[0.18, 8, 8]} />
        <meshBasicMaterial color={color} toneMapped={false} />
      </mesh>
      <mesh ref={light2}>
        <sphereGeometry args={[0.18, 8, 8]} />
        <meshBasicMaterial color={color} toneMapped={false} />
      </mesh>
      <pointLight color={color} intensity={active ? 2.8 : 1.6} distance={55} />
    </group>
  );
}


function Monument({ airportId }) {
  if (airportId === "industries") return <HongKongTower />;
  if (airportId === "process") return <EiffelTower />;
  if (airportId === "why") return <StatueOfLiberty />;
  return null;
}

/* ============================================================
   Airport - Earth-mode airport island
   ============================================================ */
function Airport({ airport }) {
  const [x, y, z] = airport.position;
  const nearestId = useWorldStore((s) => s.nearestId);
  const active = nearestId === airport.id;

  const beaconRef = useRef();
  useFrame((state) => {
    if (beaconRef.current) {
      const t = state.clock.elapsedTime;
      beaconRef.current.material.emissiveIntensity = 0.8 + Math.sin(t * 3) * 0.5 + (active ? 1 : 0);
    }
  });

  const perimeter = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      arr.push([Math.cos(a) * 14, 0.6, Math.sin(a) * 14]);
    }
    return arr;
  }, []);

  return (
    <group position={[x, y, z]}>
      {/* Underwater base */}
      <mesh position={[0, -3.5, 0]}>
        <cylinderGeometry args={[40, 46, 6, 32]} />
        <meshStandardMaterial color="#251E18" roughness={1} />
      </mesh>
      <mesh position={[0, -0.35, 0]}>
        <cylinderGeometry args={[36, 40, 1.6, 32]} />
        <meshStandardMaterial color="#C9A776" roughness={0.95} />
      </mesh>
      <mesh position={[0, 0.35, 0]}>
        <cylinderGeometry args={[30, 33, 0.9, 32]} />
        <meshStandardMaterial color="#3D5A3A" roughness={1} />
      </mesh>
      <mesh position={[0, 0.75, 0]}>
        <cylinderGeometry args={[24, 26, 0.4, 32]} />
        <meshStandardMaterial color="#4E6B48" roughness={1} />
      </mesh>
      {[0.3, 1.3, 2.4, 3.8, 4.9, 5.7].map((a, i) => {
        const r = 27 + (i % 2) * 2;
        return (
          <mesh key={`hill-${i}`} position={[Math.cos(a) * r, 0.9, Math.sin(a) * r]} scale={[1.15, 0.5, 1]}>
            <sphereGeometry args={[3 + (i % 3), 10, 7]} />
            <meshStandardMaterial color={i % 2 === 0 ? "#2F4A2F" : "#3E5638"} roughness={1} />
          </mesh>
        );
      })}
      {[0.9, 1.9, 3.2, 4.4, 5.3, 0.1, 2.7, 1.4, 3.7, 5.0].map((a, i) => {
        const r = 21 + (i % 2) * 3 + (i % 3) * 1.5;
        return (
          <group key={`tree-${i}`} position={[Math.cos(a) * r, 0.95, Math.sin(a) * r]} rotation={[0, a * 2, 0]}>
            <Tree variant={i % 3} size={0.85 + (i % 3) * 0.15} />
          </group>
        );
      })}
      {[
        { a: 4.2, r: 20, roofColor: "#B34A3A" },
        { a: 5.3, r: 18, roofColor: "#7A4A2A" },
        { a: 5.9, r: 21, roofColor: "#B34A3A" },
      ].map((h, i) => {
        const cx = Math.cos(h.a) * h.r;
        const cz = Math.sin(h.a) * h.r;
        return (
          <group key={`house-${i}`} position={[cx, 1.05, cz]} rotation={[0, h.a + Math.PI / 2, 0]}>
            <House roofColor={h.roofColor} seed={i} />
          </group>
        );
      })}
      <mesh position={[0, 1.2, 0]}>
        <cylinderGeometry args={[15, 16.5, 1.2, 6]} />
        <meshStandardMaterial color="#2A2622" roughness={0.85} metalness={0.15} />
      </mesh>
      <group position={[0, 1.7, 0]} rotation={[0, airport.runwayRot || 0, 0]}>
        <mesh position={[0, -0.02, 0]}>
          <boxGeometry args={[6, 0.05, 60]} />
          <meshStandardMaterial color="#181519" roughness={0.95} />
        </mesh>
        <mesh position={[3, -0.01, 0]}>
          <boxGeometry args={[0.3, 0.06, 60]} />
          <meshStandardMaterial color={airport.color} emissive={airport.color} emissiveIntensity={0.4} />
        </mesh>
        <mesh position={[-3, -0.01, 0]}>
          <boxGeometry args={[0.3, 0.06, 60]} />
          <meshStandardMaterial color={airport.color} emissive={airport.color} emissiveIntensity={0.4} />
        </mesh>
        {[-28, 28].map((zp, i) => (
          <group key={`thr-${i}`}>
            {[-0.9, -0.3, 0.3, 0.9].map((xo, j) => (
              <mesh key={j} position={[xo * 2.4, 0.01, zp]}>
                <boxGeometry args={[0.6, 0.06, 2.4]} />
                <meshStandardMaterial color="#EFE3D6" />
              </mesh>
            ))}
          </group>
        ))}
        {[...Array(15)].map((_, i) => (
          <mesh key={`c-${i}`} position={[0, 0.02, -25 + i * 3.6]}>
            <boxGeometry args={[0.25, 0.03, 1.8]} />
            <meshStandardMaterial color="#F4EADE" emissive="#F4EADE" emissiveIntensity={0.4} />
          </mesh>
        ))}
        {[...Array(10)].map((_, i) => {
          const zp = -27 + i * 6;
          return (
            <group key={`edge-${i}`}>
              <mesh position={[3.6, 0.2, zp]}>
                <sphereGeometry args={[0.16, 6, 6]} />
                <meshStandardMaterial color={airport.color} emissive={airport.color} emissiveIntensity={1.6} />
              </mesh>
              <mesh position={[-3.6, 0.2, zp]}>
                <sphereGeometry args={[0.16, 6, 6]} />
                <meshStandardMaterial color={airport.color} emissive={airport.color} emissiveIntensity={1.6} />
              </mesh>
            </group>
          );
        })}
        <group position={[8, 0, -18]}>
          <ControlTower color={airport.color}>
            <RotatingBeacon color={airport.color} beaconRef={beaconRef} active={active} />
          </ControlTower>
        </group>
        <group position={[-9, 0, -18]}>
          <Terminal color={airport.color} label={`${airport.code} · GREINCH`} />
        </group>
        {/* GREINCH corporate flag by the terminal */}
        <Flagpole kind="greinch" height={5} flagWidth={2.0} flagHeight={1.3} position={[-13.5, 0, -12]} phase={0.9} />
        {perimeter.slice(0, 4).map((p, i) => (
          <mesh key={i} position={[p[0] * 1.4, 0.6, p[2] * 1.4]}>
            <sphereGeometry args={[0.28, 10, 10]} />
            <meshStandardMaterial color={airport.color} emissive={airport.color} emissiveIntensity={active ? 2.4 : 1.4} />
          </mesh>
        ))}
        {[
          { x: 6, z: 14, c: "#E94B4B" }, { x: -8, z: 12, c: "#5A99CC" },
          { x: 10, z: 8, c: "#F0A020" }, { x: -6, z: -8, c: "#8B5CF6" },
          { x: 7, z: -12, c: "#22D3B4" },
        ].map((p, i) => (
          <TinyPerson key={i} x={p.x} z={p.z} color={p.c} seed={i} />
        ))}
        {[{ x: 5, z: 16 }, { x: -9, z: -6 }].map((d, i) => (
          <TinyDog key={i} x={d.x} z={d.z} seed={i + 10} />
        ))}
      </group>

      {/* Iconic monument for select airports (outside runway rotation so orientation is world-aligned) */}
      <Monument airportId={airport.id} />

      {airport.hasPort && (
        <group position={[-30, 0.6, 0]}>
          <mesh position={[0, 0.15, 0]}>
            <boxGeometry args={[14, 0.3, 3]} />
            <meshStandardMaterial color="#6B4A2A" roughness={0.95} />
          </mesh>
          {[-6, -2, 2, 6].map((xp, i) => (
            <mesh key={i} position={[xp, -0.5, 0]}>
              <cylinderGeometry args={[0.2, 0.2, 2, 6]} />
              <meshStandardMaterial color="#3A2418" roughness={1} />
            </mesh>
          ))}
          {[-4, 0, 4].map((yx, i) => (
            <group key={i} position={[yx, 0, -3]}>
              <mesh position={[0, 0.05, 0]}>
                <boxGeometry args={[2.4, 0.3, 0.9]} />
                <meshStandardMaterial color="#EFE3D6" roughness={0.85} />
              </mesh>
              <mesh position={[0.3, 0.45, 0]}>
                <boxGeometry args={[1.2, 0.5, 0.7]} />
                <meshStandardMaterial color="#F0EBE0" roughness={0.85} />
              </mesh>
              <mesh position={[-1, 0.05, 0]} rotation={[0, 0, 0.3]}>
                <coneGeometry args={[0.4, 0.6, 4]} />
                <meshStandardMaterial color="#EFE3D6" />
              </mesh>
            </group>
          ))}
        </group>
      )}

      <Billboard position={[0, 14, 0]} follow lockX={false} lockY={false} lockZ={false}>
        <Text
          fontSize={4.2}
          color={airport.color}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.06}
          outlineColor="#0B0A0C"
          letterSpacing={0.05}
          maxWidth={40}
        >
          {airport.label}
        </Text>
      </Billboard>
      <Billboard position={[0, 10.5, 0]} follow>
        <Text
          fontSize={0.9}
          color="#B9AFA6"
          anchorX="center"
          anchorY="middle"
          letterSpacing={0.35}
        >
          {`> ${airport.code} · GREINCH AIRSPACE`}
        </Text>
      </Billboard>

      {active && (
        <Html center position={[0, 7, 0]} zIndexRange={[10, 0]} distanceFactor={18}>
          <div className="pointer-events-none select-none whitespace-nowrap px-4 py-2 rounded-md bg-[rgba(11,10,12,0.85)] border border-[rgba(233,75,75,0.55)] text-[#F4EADE] font-mono text-xs uppercase tracking-[0.24em] shadow-[0_0_30px_rgba(233,75,75,0.5)]">
            <span className="text-[#E94B4B]">[E]</span> Land · {airport.label}
          </div>
        </Html>
      )}

      <pointLight color={airport.color} intensity={active ? 1.8 : 0.9} distance={45} position={[0, 3, 0]} />
    </group>
  );
}


/* ============================================================
   Ocean
   ============================================================ */
function Ocean() {
  const matRef = useRef();
  const spec1 = useRef();
  const spec2 = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (spec1.current) {
      spec1.current.position.x = Math.sin(t * 0.05) * 180;
      spec1.current.position.z = Math.cos(t * 0.05) * 180;
      spec1.current.rotation.z = t * 0.03;
    }
    if (spec2.current) {
      spec2.current.position.x = Math.cos(t * 0.04 + 1) * 220;
      spec2.current.position.z = Math.sin(t * 0.04 + 1) * 220;
      spec2.current.rotation.z = -t * 0.02;
    }
  });
  return (
    <>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.15, 0]} receiveShadow>
        <planeGeometry args={[2200, 2200, 1, 1]} />
        <meshStandardMaterial ref={matRef} color="#12466A" roughness={0.5} metalness={0.55} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 0]}>
        <planeGeometry args={[1200, 1200, 1, 1]} />
        <meshBasicMaterial color="#1D5C82" transparent opacity={0.35} depthWrite={false} />
      </mesh>
      <mesh ref={spec1} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <planeGeometry args={[380, 380]} />
        <meshBasicMaterial color="#B7D8EF" transparent opacity={0.1} depthWrite={false} />
      </mesh>
      <mesh ref={spec2} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]}>
        <planeGeometry args={[320, 320]} />
        <meshBasicMaterial color="#EAF3FF" transparent opacity={0.08} depthWrite={false} />
      </mesh>
    </>
  );
}

/* ============================================================
   Cloud shadows
   ============================================================ */
const CLOUD_POSITIONS = [
  [-80, 55, -160], [90, 65, -100], [40, 45, 120], [-140, 60, 40],
  [160, 75, 100], [0, 80, -220], [-30, 50, 180], [-190, 70, -60],
  [200, 55, -180], [-60, 90, 220], [110, 40, 200], [-220, 85, 160],
  [60, 100, -30], [-100, 35, -220],
];

function CloudShadows() {
  const groupRef = useRef();
  const projected = useMemo(() => {
    return CLOUD_POSITIONS.map((p) => {
      const t = p[1] / (90 - p[1]);
      const sx = p[0] - t * (p[0] - 180);
      const sz = p[2] - t * (p[2] + 260);
      const stretchX = 1 + Math.abs(p[0] - 180) / 220;
      const stretchZ = 1 + Math.abs(p[2] + 260) / 220;
      return { sx, sz, altitude: p[1], stretchX, stretchZ };
    });
  }, []);
  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    groupRef.current.children.forEach((child, i) => {
      const base = projected[Math.floor(i / 3)];
      if (!base) return;
      const drift = Math.sin(t * 0.08 + i) * 3;
      child.position.x = base.sx + drift + ((i % 3) - 1) * 8;
      child.position.z = base.sz + Math.cos(t * 0.06 + i) * 2 + ((i % 3) - 1) * 6;
    });
  });
  const blobs = [];
  projected.forEach((p, idx) => {
    for (let j = 0; j < 3; j++) {
      const rng = ((idx * 7 + j * 13) % 100) / 100;
      const rot = rng * Math.PI;
      const size = 14 + (j === 1 ? 4 : (j === 0 ? -3 : 0)) + rng * 6;
      blobs.push({
        p: [p.sx, 0.05, p.sz], rot,
        sx: p.stretchX * (1 + rng * 0.4),
        sy: 0.65 * p.stretchZ + rng * 0.4,
        size, opacity: j === 1 ? 0.16 : 0.1,
      });
    }
  });
  return (
    <group ref={groupRef}>
      {blobs.map((b, i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, b.rot]} position={b.p} scale={[b.sx, b.sy, 1]}>
          <circleGeometry args={[b.size, 40]} />
          <meshBasicMaterial color="#0B1A2A" transparent opacity={b.opacity} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

/* ============================================================
   Scenery - ships + rigs. Reduced count + minimum separation
   ============================================================ */
function Scenery() {
  const items = useMemo(() => {
    const rng = (seed) => {
      let x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    const arr = [];
    const MIN_SEP = 48;
    const MAX_ITEMS = 22;
    let attempts = 0;
    let idx = 0;
    while (arr.length < MAX_ITEMS && attempts < 600) {
      attempts++;
      const angle = rng(idx * 3.1 + 1) * Math.PI * 2;
      const radius = 65 + rng(idx * 7.1 + 2) * 110; // 65-175 safe from whale
      idx++;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      let tooClose = false;
      for (const ap of AIRPORTS) {
        const dx = x - ap.position[0];
        const dz = z - ap.position[2];
        if (Math.sqrt(dx * dx + dz * dz) < 68) { tooClose = true; break; }
      }
      if (tooClose) continue;
      for (const it of arr) {
        const dx = x - it.x;
        const dz = z - it.z;
        if (Math.sqrt(dx * dx + dz * dz) < MIN_SEP) { tooClose = true; break; }
      }
      if (tooClose) continue;
      // Distribution: 50% cargo, 25% sailboat, 25% speedboat, up to 3 rigs
      const rigCount = arr.filter((a) => a.kind === "rig").length;
      const roll = rng(idx * 11 + 3);
      let kind;
      if (rigCount < 3 && roll > 0.88) kind = "rig";
      else if (roll > 0.62) kind = "cargo";
      else if (roll > 0.32) kind = "sailboat";
      else kind = "speedboat";
      arr.push({
        x, z, kind,
        rot: rng(idx * 5 + 4) * Math.PI * 2,
        scale: 0.85 + rng(idx * 13) * 0.35,
        bobPhase: rng(idx * 17 + 5) * Math.PI * 2,
        bobFreq: 0.5 + rng(idx * 19 + 6) * 0.35,
      });
    }
    return arr;
  }, []);
  return (
    <group>
      {items.map((it, i) => (
        <ScenerItem key={i} data={it} />
      ))}
    </group>
  );
}

/* ============================================================
   Whale - realistic humpback using latheGeometry for smooth body
   ============================================================ */
function Whale() {
  const groupRef = useRef();
  const bodyTiltRef = useRef();
  const flukeRef = useRef();
  const pecFinLRef = useRef();
  const pecFinRRef = useRef();
  const spoutParticlesRef = useRef([]);
  const spoutState = useRef({ active: false, next: 6, t0: 0 });

  // Smooth streamlined body profile (radius as function of length)
  // Y from 0 (tail tip) to 12 (nose tip)
  const bodyProfile = useMemo(() => {
    return [
      new THREE.Vector2(0.02, 0),      // tail tip (almost zero)
      new THREE.Vector2(0.08, 0.4),
      new THREE.Vector2(0.18, 1),
      new THREE.Vector2(0.35, 1.8),    // narrow tail peduncle
      new THREE.Vector2(0.6, 2.8),
      new THREE.Vector2(0.95, 3.9),
      new THREE.Vector2(1.35, 5.1),
      new THREE.Vector2(1.65, 6.2),    // widest belly area
      new THREE.Vector2(1.78, 7.1),    // humpback bulge
      new THREE.Vector2(1.72, 7.9),
      new THREE.Vector2(1.55, 8.8),
      new THREE.Vector2(1.3, 9.6),     // head area
      new THREE.Vector2(1.0, 10.4),
      new THREE.Vector2(0.7, 11.1),
      new THREE.Vector2(0.35, 11.7),
      new THREE.Vector2(0.02, 12),     // nose tip
    ];
  }, []);

  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    // Non-linear organic movement with multiple sinusoidal layers
    const a = t * 0.055 + Math.sin(t * 0.09) * 0.35;
    const r = 205 + Math.sin(t * 0.05) * 10 + Math.cos(t * 0.11) * 6;
    const x = Math.cos(a) * r + Math.sin(t * 0.13) * 4;
    const z = Math.sin(a) * r + Math.cos(t * 0.15) * 4;
    // Dive cycle - whale surfaces about half the time now (was 30%)
    const diveCycle = (Math.sin(t * 0.16) + 1) * 0.5;
    const surfacing = diveCycle > 0.45;
    // When surfacing, whale goes noticeably above the water so its back is visible
    const y = surfacing ? -0.2 + (diveCycle - 0.45) * 3 : -3.4 - (0.45 - diveCycle) * 1.5;
    groupRef.current.position.set(x, y, z);
    // Follow motion tangent (whale points where it's going, plus a small wobble)
    const tangentAngle = -a + Math.PI / 2 + Math.sin(t * 0.3) * 0.12;
    groupRef.current.rotation.y = tangentAngle;
    if (bodyTiltRef.current) {
      const tilt = surfacing ? -0.15 : 0.08;
      bodyTiltRef.current.rotation.x = tilt + Math.sin(t * 0.5) * 0.06;
      bodyTiltRef.current.rotation.z = Math.sin(t * 0.35) * 0.08; // slight body roll
    }
    // Fluke undulation (whales pump tail vertically)
    if (flukeRef.current) {
      flukeRef.current.rotation.x = Math.sin(t * 0.9) * 0.4;
    }
    // Pec fins gentle wave
    if (pecFinLRef.current) pecFinLRef.current.rotation.z = -0.25 + Math.sin(t * 0.5) * 0.18;
    if (pecFinRRef.current) pecFinRRef.current.rotation.z = 0.25 - Math.sin(t * 0.5) * 0.18;

    // Spout logic - MUCH taller so it's visible from the air (no surfacing check - always fires periodically)
    if (spoutState.current.active) {
      const elapsed = t - spoutState.current.t0;
      if (elapsed < 4) {
        spoutParticlesRef.current.forEach((p, i) => {
          if (!p) return;
          const life = i / spoutParticlesRef.current.length;
          const localT = elapsed - life * 0.5;
          if (localT < 0 || localT > 3.5) {
            p.visible = false;
            return;
          }
          p.visible = true;
          const spreadA = (i / spoutParticlesRef.current.length) * Math.PI * 2;
          const spread = localT * 1.4;
          p.position.x = Math.cos(spreadA) * spread * 0.7;
          p.position.z = Math.sin(spreadA) * spread * 0.7;
          // Ballistic peak ~15 units, tall enough to see from the air
          p.position.y = localT * 20 - localT * localT * 6.67;
          const alpha = Math.max(0, 1 - localT / 3.5);
          if (p.material) p.material.opacity = alpha * 0.95;
          p.scale.setScalar(0.8 + localT * 0.8);
        });
      } else {
        spoutParticlesRef.current.forEach((p) => { if (p) p.visible = false; });
        spoutState.current.active = false;
        spoutState.current.next = t + 8 + Math.random() * 6; // every ~8-14s
      }
    } else if (t > spoutState.current.next) {
      // Fire spout regardless of dive cycle - always visible
      spoutState.current.active = true;
      spoutState.current.t0 = t;
    }
  });

  return (
    <group ref={groupRef}>
      <group ref={bodyTiltRef}>
        {/* Main body - lathe-generated smooth streamlined shape, laid along Z
            Native lathe axis is Y. We rotate -90° around X and shift so whale center is at origin.
            Head at -Z (front), tail at +Z (rear). */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 6]}>
          <latheGeometry args={[bodyProfile, 28]} />
          <meshStandardMaterial color="#2A3F52" roughness={0.75} metalness={0.12} />
        </mesh>
        {/* Belly counter-shading: lighter underside (a slightly smaller lathe, offset down) */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.35, 6]} scale={[0.92, 0.55, 0.92]}>
          <latheGeometry args={[bodyProfile, 24]} />
          <meshStandardMaterial color="#8AA5B4" roughness={0.8} transparent opacity={0.95} />
        </mesh>
        {/* Ventral throat grooves (humpback signature) - three thin lines under front */}
        {[-0.35, 0, 0.35].map((xo, i) => (
          <mesh key={`gr-${i}`} position={[xo, -0.85, -3]} rotation={[0, 0, 0]}>
            <boxGeometry args={[0.08, 0.04, 5]} />
            <meshStandardMaterial color="#0B1418" roughness={1} />
          </mesh>
        ))}
        {/* Mouth line (dark curved slit near nose) */}
        <mesh position={[0.55, -0.35, -5.3]} rotation={[0, 0.2, 0]}>
          <boxGeometry args={[0.06, 0.06, 1.6]} />
          <meshStandardMaterial color="#0B0A0C" />
        </mesh>
        <mesh position={[-0.55, -0.35, -5.3]} rotation={[0, -0.2, 0]}>
          <boxGeometry args={[0.06, 0.06, 1.6]} />
          <meshStandardMaterial color="#0B0A0C" />
        </mesh>
        {/* Small tubercles on head (bumps characteristic of humpback) */}
        {[[0.6, 0.4, -5], [-0.6, 0.4, -5], [0.5, 0.7, -4.2], [-0.5, 0.7, -4.2], [0, 0.9, -4.6], [0, 0.9, -5.4]].map((p, i) => (
          <mesh key={`tub-${i}`} position={p}>
            <sphereGeometry args={[0.12, 8, 6]} />
            <meshStandardMaterial color="#243545" roughness={0.9} />
          </mesh>
        ))}
        {/* Eyes */}
        <mesh position={[1.2, -0.05, -4.2]}>
          <sphereGeometry args={[0.18, 10, 8]} />
          <meshStandardMaterial color="#0B0A0C" roughness={0.15} metalness={0.5} />
        </mesh>
        <mesh position={[-1.2, -0.05, -4.2]}>
          <sphereGeometry args={[0.18, 10, 8]} />
          <meshStandardMaterial color="#0B0A0C" roughness={0.15} metalness={0.5} />
        </mesh>
        {/* Small dorsal fin (positioned about 2/3 back on body top) */}
        <mesh position={[0, 1.5, 2.5]} rotation={[0.35, 0, 0]}>
          <coneGeometry args={[0.55, 1.4, 4]} />
          <meshStandardMaterial color="#243545" roughness={0.75} />
        </mesh>
        {/* Blowhole */}
        <mesh position={[0, 1.65, -2.5]}>
          <cylinderGeometry args={[0.18, 0.22, 0.1, 12]} />
          <meshStandardMaterial color="#0B0A0C" roughness={0.35} />
        </mesh>
        {/* Long pectoral fins - humpback signature paddle-shaped flippers (organic ellipsoids) */}
        <group ref={pecFinLRef} position={[1.6, -0.3, -1.5]} rotation={[0, 0, -0.25]}>
          <mesh position={[1.9, -0.3, 0.6]} rotation={[0, -0.5, -0.15]} scale={[3.4, 0.22, 1.0]}>
            <sphereGeometry args={[1, 16, 10]} />
            <meshStandardMaterial color="#2A3F52" roughness={0.75} />
          </mesh>
          {/* White underside of fin */}
          <mesh position={[1.9, -0.4, 0.6]} rotation={[0, -0.5, -0.15]} scale={[3.35, 0.15, 0.98]}>
            <sphereGeometry args={[1, 14, 10]} />
            <meshStandardMaterial color="#EFE3D6" roughness={0.85} />
          </mesh>
          {/* Bumps along the leading edge (characteristic humpback tubercles) */}
          {[-2.5, -1.6, -0.7, 0.2, 1.1].map((zo, i) => {
            const x = 1.9 - zo * 0.15;
            const z = 0.6 + zo;
            return (
              <mesh key={i} position={[x, -0.2, z - 0.4]} scale={[0.7, 0.18, 0.55]}>
                <sphereGeometry args={[0.2, 8, 6]} />
                <meshStandardMaterial color="#2A3F52" roughness={0.8} />
              </mesh>
            );
          })}
        </group>
        <group ref={pecFinRRef} position={[-1.6, -0.3, -1.5]} rotation={[0, 0, 0.25]}>
          <mesh position={[-1.9, -0.3, 0.6]} rotation={[0, 0.5, 0.15]} scale={[3.4, 0.22, 1.0]}>
            <sphereGeometry args={[1, 16, 10]} />
            <meshStandardMaterial color="#2A3F52" roughness={0.75} />
          </mesh>
          <mesh position={[-1.9, -0.4, 0.6]} rotation={[0, 0.5, 0.15]} scale={[3.35, 0.15, 0.98]}>
            <sphereGeometry args={[1, 14, 10]} />
            <meshStandardMaterial color="#EFE3D6" roughness={0.85} />
          </mesh>
          {[-2.5, -1.6, -0.7, 0.2, 1.1].map((zo, i) => {
            const x = -1.9 + zo * 0.15;
            const z = 0.6 + zo;
            return (
              <mesh key={i} position={[x, -0.2, z - 0.4]} scale={[0.7, 0.18, 0.55]}>
                <sphereGeometry args={[0.2, 8, 6]} />
                <meshStandardMaterial color="#2A3F52" roughness={0.8} />
              </mesh>
            );
          })}
        </group>
        {/* Fluke - horizontal whale tail with organic curved lobes */}
        <group ref={flukeRef} position={[0, 0, 6.2]}>
          {/* Left lobe - curved ellipsoid */}
          <mesh position={[-1.7, 0, 0.4]} rotation={[0, 0.5, 0.05]} scale={[2.6, 0.15, 1.5]}>
            <sphereGeometry args={[1, 14, 10]} />
            <meshStandardMaterial color="#243545" roughness={0.75} />
          </mesh>
          {/* Right lobe */}
          <mesh position={[1.7, 0, 0.4]} rotation={[0, -0.5, -0.05]} scale={[2.6, 0.15, 1.5]}>
            <sphereGeometry args={[1, 14, 10]} />
            <meshStandardMaterial color="#243545" roughness={0.75} />
          </mesh>
          {/* Center notch (connection between lobes - narrow) */}
          <mesh position={[0, 0, 0.2]} scale={[0.6, 0.13, 0.9]}>
            <sphereGeometry args={[1, 10, 8]} />
            <meshStandardMaterial color="#243545" roughness={0.75} />
          </mesh>
          {/* Underside lighter tone */}
          <mesh position={[-1.7, -0.08, 0.4]} rotation={[0, 0.5, 0.05]} scale={[2.5, 0.08, 1.45]}>
            <sphereGeometry args={[1, 12, 8]} />
            <meshStandardMaterial color="#8AA5B4" roughness={0.85} />
          </mesh>
          <mesh position={[1.7, -0.08, 0.4]} rotation={[0, -0.5, -0.05]} scale={[2.5, 0.08, 1.45]}>
            <sphereGeometry args={[1, 12, 8]} />
            <meshStandardMaterial color="#8AA5B4" roughness={0.85} />
          </mesh>
        </group>
      </group>
      {/* Water spout particles - OUTSIDE bodyTilt so they stay vertical regardless of pitch */}
      <group position={[0, 1.7, -2.5]}>
        {[...Array(24)].map((_, i) => (
          <mesh key={i} ref={(el) => (spoutParticlesRef.current[i] = el)} visible={false}>
            <sphereGeometry args={[0.4, 8, 8]} />
            <meshBasicMaterial color="#EAF3FF" transparent opacity={0.95} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
      </group>
    </group>
  );
}


function ScenerItem({ data }) {
  const ref = useRef();
  const wakeRef = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    // Speedboats plane on top of water: minimal vertical bob, sharp horizontal wake
    const isSpeedboat = data.kind === "speedboat";
    const bobAmp = isSpeedboat ? 0.06 : 0.35;
    const bob = Math.sin(t * data.bobFreq + data.bobPhase) * bobAmp;
    const roll = Math.sin(t * data.bobFreq * 0.7 + data.bobPhase + 1) * (isSpeedboat ? 0.03 : 0.06);
    const pitch = isSpeedboat
      ? 0.18 + Math.cos(t * data.bobFreq * 0.5 + data.bobPhase) * 0.02 // planing nose-up
      : Math.cos(t * data.bobFreq * 0.5 + data.bobPhase) * 0.04;
    ref.current.position.y = 0.2 + bob;
    ref.current.rotation.z = roll;
    ref.current.rotation.x = pitch;
    // Ship movement - move along their heading, loop in a big radius
    const moveSpeed = data.kind === "speedboat" ? 4.5 : (data.kind === "sailboat" ? 1.2 : (data.kind === "cargo" || data.kind === "ship" ? 0.5 : 0));
    if (moveSpeed > 0) {
      const dirX = -Math.sin(data.rot);
      const dirZ = -Math.cos(data.rot);
      const distTraveled = (t * moveSpeed) % 200 - 100; // -100 to +100 loop
      ref.current.position.x = data.x + dirX * distTraveled;
      ref.current.position.z = data.z + dirZ * distTraveled;
    }
    // Speedboat wake (bubbling foam behind)
    if (wakeRef.current) {
      wakeRef.current.material.opacity = 0.35 + Math.abs(Math.sin(t * 8)) * 0.25;
      wakeRef.current.scale.setScalar(1 + Math.sin(t * 4) * 0.1);
    }
  });
  const it = data;
  return (
    <group ref={ref} position={[it.x, 0.2, it.z]} scale={it.scale}>
      <group rotation={[0, it.rot, 0]}>
        {/* Wake for speedboats */}
        {it.kind === "speedboat" && (
          <mesh ref={wakeRef} position={[-3.5, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[8, 3]} />
            <meshBasicMaterial color="#FFFFFF" transparent opacity={0.5} depthWrite={false} />
          </mesh>
        )}
        {it.kind === "cargo" || it.kind === "ship" ? (
          <>
            <mesh position={[0, 0.7, 0]}>
              <boxGeometry args={[22, 1.6, 4.5]} />
              <meshStandardMaterial color="#1A2530" roughness={0.85} />
            </mesh>
            <mesh position={[0, 0.05, 0]}>
              <boxGeometry args={[22.1, 0.35, 4.55]} />
              <meshStandardMaterial color="#8A2A2A" roughness={0.8} />
            </mesh>
            <mesh position={[-8.5, 2.4, 0]}>
              <boxGeometry args={[3.5, 2.2, 3.6]} />
              <meshStandardMaterial color="#F0EBE0" roughness={0.85} />
            </mesh>
            <mesh position={[-8.5, 3.1, 0]}>
              <boxGeometry args={[3.6, 0.4, 3.7]} />
              <meshStandardMaterial color="#1A1A22" metalness={0.6} roughness={0.2} />
            </mesh>
            <mesh position={[-8.5, 4, 0]}>
              <boxGeometry args={[1.2, 1.6, 1.6]} />
              <meshStandardMaterial color="#6B1F47" roughness={0.9} />
            </mesh>
            <mesh position={[-8.5, 4.85, 0]}>
              <boxGeometry args={[1, 0.15, 1.4]} />
              <meshStandardMaterial color="#E94B4B" emissive="#E94B4B" emissiveIntensity={0.35} />
            </mesh>
            {[
              { x: -3, c1: "#E94B4B", c2: "#EFE3D6" },
              { x: 0.5, c1: "#3A6BA5", c2: "#B3641C" },
              { x: 4, c1: "#2E8B57", c2: "#EFE3D6" },
            ].map((row, ri) => (
              <group key={ri}>
                <mesh position={[row.x, 2, 0]}>
                  <boxGeometry args={[2.6, 1.2, 3.6]} />
                  <meshStandardMaterial color={row.c1} roughness={0.8} />
                </mesh>
                <mesh position={[row.x, 3.15, 0]}>
                  <boxGeometry args={[2.6, 1.1, 3.6]} />
                  <meshStandardMaterial color={row.c2} roughness={0.85} />
                </mesh>
              </group>
            ))}
            <mesh position={[8.5, 3.2, 0]}>
              <cylinderGeometry args={[0.12, 0.12, 3.6, 6]} />
              <meshStandardMaterial color="#B9AFA6" roughness={0.7} metalness={0.3} />
            </mesh>
            <mesh position={[9.8, 4.4, 0]} rotation={[0, 0, -0.5]}>
              <boxGeometry args={[3, 0.2, 0.4]} />
              <meshStandardMaterial color="#B9AFA6" roughness={0.7} metalness={0.3} />
            </mesh>
          </>
        ) : it.kind === "sailboat" ? (
          <>
            {/* Sleek fiberglass hull - stretched sphere for smooth curves */}
            <mesh position={[0, 0.35, 0]} scale={[4.4, 0.5, 1.05]}>
              <sphereGeometry args={[1, 20, 12]} />
              <meshStandardMaterial color="#F4EADE" roughness={0.55} metalness={0.2} />
            </mesh>
            {/* Dark waterline stripe */}
            <mesh position={[0, 0.05, 0]} scale={[4.45, 0.12, 1.1]}>
              <sphereGeometry args={[1, 20, 8]} />
              <meshStandardMaterial color="#1A2530" roughness={0.9} />
            </mesh>
            {/* Bottom keel (dark antifouling) */}
            <mesh position={[0, -0.2, 0]} scale={[4.2, 0.4, 0.95]}>
              <sphereGeometry args={[1, 16, 10]} />
              <meshStandardMaterial color="#3A2A2E" roughness={0.9} />
            </mesh>
            {/* Wooden deck */}
            <mesh position={[0, 0.75, 0]} scale={[4.15, 0.06, 0.95]}>
              <sphereGeometry args={[1, 20, 8]} />
              <meshStandardMaterial color="#B8825A" roughness={0.85} />
            </mesh>
            {/* Cabin (raised, low profile) */}
            <mesh position={[-0.6, 1.1, 0]}>
              <boxGeometry args={[2.6, 0.55, 1.3]} />
              <meshStandardMaterial color="#F4EADE" roughness={0.75} />
            </mesh>
            {/* Cabin dark window band */}
            <mesh position={[-0.6, 1.15, 0]}>
              <boxGeometry args={[2.7, 0.22, 1.34]} />
              <meshStandardMaterial color="#0F1520" metalness={0.9} roughness={0.05} emissive="#5A99CC" emissiveIntensity={0.35} toneMapped={false} />
            </mesh>
            {/* Cockpit opening (aft) */}
            <mesh position={[-2, 0.85, 0]}>
              <boxGeometry args={[1.4, 0.35, 1.1]} />
              <meshStandardMaterial color="#2A1F1C" />
            </mesh>
            {/* Steering wheel */}
            <mesh position={[-1.8, 1.15, 0]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[0.22, 0.03, 6, 12]} />
              <meshStandardMaterial color="#B9AFA6" metalness={0.9} roughness={0.1} />
            </mesh>
            {/* Aluminum mast (tall) */}
            <mesh position={[0.4, 4.6, 0]}>
              <cylinderGeometry args={[0.07, 0.09, 8.5, 10]} />
              <meshStandardMaterial color="#D8D0C4" metalness={0.85} roughness={0.15} />
            </mesh>
            {/* Boom (horizontal along X axis, at gooseneck height) */}
            <mesh position={[-1.5, 1.55, 0]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.06, 0.06, 4, 6]} />
              <meshStandardMaterial color="#D8D0C4" metalness={0.85} roughness={0.15} />
            </mesh>
            {/* Mainsail (curved, wind-filled triangle) */}
            <mesh position={[0.4, 1.55, 0.02]} rotation={[0, Math.PI / 2, 0]}>
              <shapeGeometry args={[CURVED_MAIN_SAIL]} />
              <meshStandardMaterial color="#FFFFFF" roughness={0.9} side={THREE.DoubleSide} />
            </mesh>
            {/* Jib (front curved triangle) */}
            <mesh position={[0.4, 1.5, -0.02]} rotation={[0, Math.PI / 2, 0]}>
              <shapeGeometry args={[CURVED_JIB_SAIL]} />
              <meshStandardMaterial color="#F4EADE" roughness={0.9} side={THREE.DoubleSide} />
            </mesh>
            {/* Forestay (thin wire from mast top to bow) */}
            <mesh position={[1.6, 3.2, 0]} rotation={[0, 0, -0.62]}>
              <cylinderGeometry args={[0.02, 0.02, 6, 4]} />
              <meshStandardMaterial color="#6B6660" />
            </mesh>
            {/* Backstay */}
            <mesh position={[-1.6, 4, 0]} rotation={[0, 0, 0.5]}>
              <cylinderGeometry args={[0.02, 0.02, 8, 4]} />
              <meshStandardMaterial color="#6B6660" />
            </mesh>
            {/* Wind vane at masthead */}
            <mesh position={[0.4, 8.85, 0]}>
              <boxGeometry args={[0.4, 0.05, 0.02]} />
              <meshStandardMaterial color="#E94B4B" />
            </mesh>
            {/* Nav lights */}
            <mesh position={[3.6, 0.75, 0.55]}>
              <sphereGeometry args={[0.08, 6, 6]} />
              <meshBasicMaterial color="#2E8B57" toneMapped={false} />
            </mesh>
            <mesh position={[3.6, 0.75, -0.55]}>
              <sphereGeometry args={[0.08, 6, 6]} />
              <meshBasicMaterial color="#E94B4B" toneMapped={false} />
            </mesh>
            {/* Red boot-top stripe */}
            <mesh position={[0, 0.22, 0]} scale={[4.4, 0.06, 1.06]}>
              <sphereGeometry args={[1, 20, 8]} />
              <meshStandardMaterial color="#B01A2E" emissive="#B01A2E" emissiveIntensity={0.35} />
            </mesh>
          </>
        ) : it.kind === "speedboat" ? (
          <>
            {/* Planing hull - stretched sphere with sharp bow taper */}
            <mesh position={[0, 0.3, 0]} scale={[3.2, 0.42, 0.85]}>
              <sphereGeometry args={[1, 18, 10]} />
              <meshStandardMaterial color="#F4EADE" roughness={0.35} metalness={0.4} />
            </mesh>
            {/* Sharp pointed bow (elongated cone laid on side) */}
            <mesh position={[2.6, 0.3, 0]} rotation={[0, 0, -Math.PI / 2]}>
              <coneGeometry args={[0.7, 2.4, 16]} />
              <meshStandardMaterial color="#F4EADE" roughness={0.35} metalness={0.4} />
            </mesh>
            {/* Deck (dark) */}
            <mesh position={[-0.3, 0.62, 0]} scale={[2.9, 0.06, 0.72]}>
              <sphereGeometry args={[1, 18, 8]} />
              <meshStandardMaterial color="#1A2530" roughness={0.6} />
            </mesh>
            {/* Bright racing stripe */}
            <mesh position={[0, 0.15, 0]} scale={[3.22, 0.08, 0.87]}>
              <sphereGeometry args={[1, 18, 8]} />
              <meshStandardMaterial color="#F0A020" emissive="#F0A020" emissiveIntensity={0.55} toneMapped={false} />
            </mesh>
            {/* Angled tinted windshield */}
            <mesh position={[0.5, 1.0, 0]} rotation={[0, 0, -0.45]}>
              <boxGeometry args={[0.15, 0.9, 1.35]} />
              <meshStandardMaterial color="#0F0D11" metalness={0.95} roughness={0.05} emissive="#5A99CC" emissiveIntensity={0.35} toneMapped={false} />
            </mesh>
            {/* Chrome windshield frame top */}
            <mesh position={[0.35, 1.4, 0]}>
              <boxGeometry args={[0.35, 0.05, 1.4]} />
              <meshStandardMaterial color="#EFE3D6" metalness={0.95} roughness={0.05} />
            </mesh>
            {/* Cockpit seats (twin) */}
            <mesh position={[-0.5, 0.85, 0.35]}>
              <boxGeometry args={[0.9, 0.5, 0.5]} />
              <meshStandardMaterial color="#3A2A2E" roughness={0.9} />
            </mesh>
            <mesh position={[-0.5, 0.85, -0.35]}>
              <boxGeometry args={[0.9, 0.5, 0.5]} />
              <meshStandardMaterial color="#3A2A2E" roughness={0.9} />
            </mesh>
            {/* Rear bench seat */}
            <mesh position={[-1.9, 0.85, 0]}>
              <boxGeometry args={[0.6, 0.5, 1.3]} />
              <meshStandardMaterial color="#3A2A2E" roughness={0.9} />
            </mesh>
            {/* Steering wheel */}
            <mesh position={[-0.05, 1.05, 0.35]} rotation={[Math.PI / 2.4, 0, 0]}>
              <torusGeometry args={[0.14, 0.025, 6, 12]} />
              <meshStandardMaterial color="#B9AFA6" metalness={0.9} roughness={0.1} />
            </mesh>
            {/* Outboard motor mount */}
            <mesh position={[-2.9, 0.5, 0]}>
              <boxGeometry args={[0.35, 0.9, 0.55]} />
              <meshStandardMaterial color="#0B0A0C" metalness={0.85} roughness={0.3} />
            </mesh>
            {/* Motor head (large) */}
            <mesh position={[-3.1, 0.9, 0]}>
              <boxGeometry args={[0.6, 0.7, 0.7]} />
              <meshStandardMaterial color="#1A1A22" metalness={0.6} roughness={0.35} />
            </mesh>
            {/* Motor lower unit */}
            <mesh position={[-3.05, -0.05, 0]}>
              <boxGeometry args={[0.25, 0.7, 0.25]} />
              <meshStandardMaterial color="#0B0A0C" metalness={0.85} />
            </mesh>
            {/* Propeller housing */}
            <mesh position={[-3.05, -0.35, 0]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.15, 0.15, 0.35, 8]} />
              <meshStandardMaterial color="#8B8681" metalness={0.9} roughness={0.15} />
            </mesh>
            {/* Bow cleat / nav light */}
            <mesh position={[3.4, 0.75, 0]}>
              <sphereGeometry args={[0.09, 8, 8]} />
              <meshBasicMaterial color="#EAF3FF" toneMapped={false} />
            </mesh>
            {/* Twin exhaust ports */}
            <mesh position={[-2.9, 0.2, 0.25]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.08, 0.08, 0.15, 8]} />
              <meshStandardMaterial color="#2A1F1C" />
            </mesh>
            <mesh position={[-2.9, 0.2, -0.25]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.08, 0.08, 0.15, 8]} />
              <meshStandardMaterial color="#2A1F1C" />
            </mesh>
          </>
        ) : (
          <>
            {[[-2, 2], [2, 2], [-2, -2], [2, -2]].map(([lx, lz], li) => (
              <mesh key={li} position={[lx, 1.8, lz]}>
                <cylinderGeometry args={[0.25, 0.35, 3.6, 6]} />
                <meshStandardMaterial color="#3A2A2E" roughness={0.85} />
              </mesh>
            ))}
            <mesh position={[0, 3.7, 0]}>
              <boxGeometry args={[5.5, 0.5, 5.5]} />
              <meshStandardMaterial color="#2A3040" roughness={0.85} />
            </mesh>
            <mesh position={[0, 4.6, 0]}>
              <boxGeometry args={[3, 1.6, 3]} />
              <meshStandardMaterial color="#EFE3D6" roughness={0.85} />
            </mesh>
            <mesh position={[0, 6.8, 0]}>
              <cylinderGeometry args={[0.15, 0.15, 3, 6]} />
              <meshStandardMaterial color="#3A2A2E" roughness={0.85} />
            </mesh>
            <mesh position={[0, 8.4, 0]}>
              <sphereGeometry args={[0.35, 8, 8]} />
              <meshStandardMaterial color="#E94B4B" emissive="#E94B4B" emissiveIntensity={1.4} />
            </mesh>
          </>
        )}
      </group>
    </group>
  );
}

/* ============================================================
   Sky (Earth) - realistic daytime with sun, clouds and scattering
   ============================================================ */
const SUN_WORLD_POS = [180, 90, -260];
const SUN_DIR = (() => {
  const l = Math.hypot(...SUN_WORLD_POS);
  return [SUN_WORLD_POS[0] / l, SUN_WORLD_POS[1] / l, SUN_WORLD_POS[2] / l];
})();

function Sun() {
  const meshRef = useRef();
  const glowRef = useRef();
  const setDazzle = useWorldStore((s) => s.setDazzle);
  const setCameraDir = useWorldStore((s) => s.setCameraDir);
  const tmp = useMemo(() => new THREE.Vector3(), []);

  useFrame((state) => {
    state.camera.getWorldDirection(tmp);
    setCameraDir({ x: tmp.x, y: tmp.y, z: tmp.z });
    const dot = tmp.x * SUN_DIR[0] + tmp.y * SUN_DIR[1] + tmp.z * SUN_DIR[2];
    const daz = Math.max(0, (dot - 0.82) / 0.18);
    setDazzle(daz);

    if (glowRef.current) {
      const t = state.clock.elapsedTime;
      glowRef.current.scale.setScalar(1 + Math.sin(t * 1.5) * 0.05);
    }
  });

  return (
    <group position={SUN_WORLD_POS}>
      <mesh ref={meshRef}>
        <sphereGeometry args={[14, 32, 32]} />
        <meshBasicMaterial color="#FFF6C8" toneMapped={false} />
      </mesh>
      <mesh ref={glowRef}>
        <sphereGeometry args={[24, 32, 32]} />
        <meshBasicMaterial color="#FFDF7A" transparent opacity={0.35} toneMapped={false} />
      </mesh>
      <pointLight color="#FFE9A8" intensity={2.8} distance={0} decay={0} />
    </group>
  );
}

function Sky() {
  return (
    <>
      <color attach="background" args={["#8FC5EA"]} />
      <fog attach="fog" args={["#B7D8EF", 160, 620]} />
      <DreiSky
        distance={4500}
        sunPosition={SUN_WORLD_POS}
        inclination={0.6}
        azimuth={0.25}
        turbidity={6}
        rayleigh={2}
        mieCoefficient={0.005}
        mieDirectionalG={0.85}
      />
      <Sun />
      <Clouds material={THREE.MeshBasicMaterial} limit={400}>
        <Cloud seed={1} position={[-80, 55, -160]} bounds={[40, 8, 40]} volume={22} color="#FFFFFF" opacity={0.65} speed={0.05} growth={4} />
        <Cloud seed={2} position={[90, 65, -100]} bounds={[50, 10, 50]} volume={28} color="#FFFFFF" opacity={0.55} speed={0.04} growth={4} />
        <Cloud seed={3} position={[40, 45, 120]} bounds={[35, 8, 35]} volume={20} color="#F4F1E8" opacity={0.5} speed={0.06} growth={3.5} />
        <Cloud seed={4} position={[-140, 60, 40]} bounds={[45, 9, 45]} volume={25} color="#FFFFFF" opacity={0.6} speed={0.05} growth={4} />
        <Cloud seed={5} position={[160, 75, 100]} bounds={[45, 10, 45]} volume={26} color="#FFFFFF" opacity={0.55} speed={0.05} growth={4} />
        <Cloud seed={6} position={[0, 80, -220]} bounds={[60, 12, 60]} volume={32} color="#FFFFFF" opacity={0.5} speed={0.03} growth={4.5} />
        <Cloud seed={7} position={[-30, 50, 180]} bounds={[38, 8, 38]} volume={22} color="#F0EBE0" opacity={0.55} speed={0.05} growth={3.5} />
        <Cloud seed={8} position={[-190, 70, -60]} bounds={[52, 11, 52]} volume={28} color="#FFFFFF" opacity={0.5} speed={0.04} growth={4} />
        <Cloud seed={9} position={[200, 55, -180]} bounds={[45, 9, 45]} volume={24} color="#FFFFFF" opacity={0.55} speed={0.05} growth={4} />
        <Cloud seed={10} position={[-60, 90, 220]} bounds={[55, 11, 55]} volume={30} color="#F0EBE0" opacity={0.45} speed={0.03} growth={4.5} />
        <Cloud seed={11} position={[110, 40, 200]} bounds={[40, 8, 40]} volume={22} color="#FFFFFF" opacity={0.55} speed={0.06} growth={3.5} />
        <Cloud seed={12} position={[-220, 85, 160]} bounds={[50, 10, 50]} volume={26} color="#FFFFFF" opacity={0.5} speed={0.04} growth={4} />
        <Cloud seed={13} position={[60, 100, -30]} bounds={[42, 9, 42]} volume={24} color="#F4F1E8" opacity={0.5} speed={0.04} growth={4} />
        <Cloud seed={14} position={[-100, 35, -220]} bounds={[38, 7, 38]} volume={20} color="#FFFFFF" opacity={0.6} speed={0.05} growth={3.5} />
      </Clouds>
      <Stars radius={480} depth={80} count={800} factor={4} saturation={0} fade speed={0.3} />
    </>
  );
}

/* ============================================================
   Space sky - dark, huge starfield, distant nebula
   ============================================================ */
function SpaceSky() {
  return (
    <>
      <color attach="background" args={["#020108"]} />
      <fog attach="fog" args={["#060316", 320, 1000]} />
      {/* Dense star field - many small stars */}
      <Stars radius={520} depth={140} count={12000} factor={5} saturation={0.15} fade speed={0.4} />
      {/* Brighter foreground stars */}
      <Stars radius={280} depth={80} count={3500} factor={3} saturation={0.55} fade speed={1.1} />
      {/* Milky Way band - large stretched cloud across the sky */}
      <MilkyWay />
      {/* Distant nebulae */}
      <Nebula position={[400, 100, -450]} scale={1.4} color1="#8B2E8B" color2="#3A1A55" />
      <Nebula position={[-500, -60, 250]} scale={1.8} color1="#2E5A8B" color2="#1A2A55" />
      <Nebula position={[150, 220, 550]} scale={1.1} color1="#8B2A2A" color2="#3A1A1A" />
      <Nebula position={[-320, 180, -220]} scale={0.9} color1="#2A8B6A" color2="#1A3A2A" />
      {/* Distant galaxy spirals */}
      <SpiralGalaxy position={[520, 150, -650]} scale={1.2} tint="#F0D890" />
      <SpiralGalaxy position={[-620, -80, 400]} scale={1.5} tint="#C9A8FF" rotation={[0.3, 0.8, 0.4]} />
      <SpiralGalaxy position={[220, -220, 620]} scale={0.9} tint="#9AC5FF" rotation={[-0.6, 0.2, -0.3]} />
      {/* Distant sun/star (main lighting source) */}
      <mesh position={[600, 100, -700]}>
        <sphereGeometry args={[26, 24, 24]} />
        <meshBasicMaterial color="#FFF6C8" toneMapped={false} />
      </mesh>
      <mesh position={[600, 100, -700]}>
        <sphereGeometry args={[55, 24, 24]} />
        <meshBasicMaterial color="#FFDF7A" transparent opacity={0.32} depthWrite={false} toneMapped={false} />
      </mesh>
      {/* Famous named stars with distance labels */}
      <NamedStar position={[-380, 220, -520]} name="SIRIUS" distance="8.6 LY" color="#EAF3FF" size={4} />
      <NamedStar position={[420, 280, 350]} name="BETELGEUSE" distance="642 LY" color="#FF6B4A" size={5.5} />
      <NamedStar position={[-560, 60, 480]} name="VEGA" distance="25 LY" color="#B8E3FF" size={3.5} />
      <NamedStar position={[280, -180, -560]} name="POLARIS" distance="433 LY" color="#FFFDD0" size={4} />
      <NamedStar position={[520, -60, 200]} name="ALPHA CENTAURI" distance="4.37 LY" color="#F0E890" size={3.8} />
      <NamedStar position={[-260, 320, 180]} name="ANTARES" distance="550 LY" color="#FF4A2A" size={4.6} />
      {/* International Space Station orbiting Earth-return planet */}
      <ISS orbitCenter={[-30, 20, 240]} orbitRadius={38} orbitSpeed={0.35} />
    </>
  );
}

/* Milky Way band - a stretched, tilted glowing cloud across the sky */
function MilkyWay() {
  return (
    <group rotation={[0.3, 0.7, 0.45]}>
      {/* Main galactic band */}
      <mesh position={[0, 0, -500]}>
        <planeGeometry args={[1400, 220]} />
        <meshBasicMaterial color="#B8C4E8" transparent opacity={0.09} depthWrite={false} toneMapped={false} />
      </mesh>
      {/* Denser core */}
      <mesh position={[0, 0, -500]}>
        <planeGeometry args={[900, 130]} />
        <meshBasicMaterial color="#EFE3D6" transparent opacity={0.12} depthWrite={false} toneMapped={false} />
      </mesh>
      {/* Dark dust lane */}
      <mesh position={[0, -8, -498]}>
        <planeGeometry args={[1100, 30]} />
        <meshBasicMaterial color="#050308" transparent opacity={0.55} depthWrite={false} />
      </mesh>
      {/* Bright galactic bulge (Sagittarius region) */}
      <mesh position={[-150, 5, -497]}>
        <sphereGeometry args={[65, 20, 16]} />
        <meshBasicMaterial color="#FFEBA8" transparent opacity={0.18} depthWrite={false} toneMapped={false} />
      </mesh>
    </group>
  );
}

/* Nebula - layered translucent spheres for a soft gaseous cloud */
function Nebula({ position, scale = 1, color1, color2 }) {
  return (
    <group position={position} scale={scale}>
      <mesh>
        <sphereGeometry args={[180, 20, 16]} />
        <meshBasicMaterial color={color1} transparent opacity={0.14} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh position={[40, 20, 0]}>
        <sphereGeometry args={[130, 18, 14]} />
        <meshBasicMaterial color={color2} transparent opacity={0.18} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh position={[-30, -15, 20]}>
        <sphereGeometry args={[90, 16, 12]} />
        <meshBasicMaterial color={color1} transparent opacity={0.22} depthWrite={false} toneMapped={false} />
      </mesh>
      {/* Bright core */}
      <mesh position={[10, 5, -10]}>
        <sphereGeometry args={[30, 14, 12]} />
        <meshBasicMaterial color="#F4EADE" transparent opacity={0.35} depthWrite={false} toneMapped={false} />
      </mesh>
    </group>
  );
}

/* Spiral galaxy - realistic top-down disk with bulge and arms */
function SpiralGalaxy({ position, scale = 1, tint = "#FFFFFF", rotation = [Math.PI / 2, 0, 0] }) {
  const ref = useRef();
  useFrame((state) => {
    if (ref.current) ref.current.rotation.z = state.clock.elapsedTime * 0.008;
  });
  return (
    <group position={position} scale={scale} rotation={rotation}>
      <group ref={ref}>
        {/* Faint outer halo */}
        <mesh>
          <ringGeometry args={[0, 140, 40]} />
          <meshBasicMaterial color={tint} transparent opacity={0.06} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
        {/* Main disk */}
        <mesh>
          <ringGeometry args={[10, 95, 40]} />
          <meshBasicMaterial color={tint} transparent opacity={0.18} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
        {/* Spiral arms (approximated with rotated stretched planes) */}
        {[0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2].map((rz, i) => (
          <mesh key={i} rotation={[0, 0, rz + 0.4]}>
            <planeGeometry args={[180, 22]} />
            <meshBasicMaterial color={tint} transparent opacity={0.09} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
          </mesh>
        ))}
        {/* Central bulge */}
        <mesh>
          <sphereGeometry args={[18, 16, 12]} />
          <meshBasicMaterial color="#FFEBA8" transparent opacity={0.45} depthWrite={false} toneMapped={false} />
        </mesh>
        <mesh>
          <sphereGeometry args={[9, 14, 10]} />
          <meshBasicMaterial color="#FFF6C8" toneMapped={false} />
        </mesh>
        {/* Dark dust lane across the disk (small arc segment) */}
        <mesh>
          <ringGeometry args={[30, 55, 40, 1, 0.2, 0.6]} />
          <meshBasicMaterial color="#050308" transparent opacity={0.55} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
      </group>
    </group>
  );
}

/* Named star with distance label */
function NamedStar({ position, name, distance, color, size = 3 }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    ref.current.material.opacity = 0.85 + Math.sin(t * 2 + position[0]) * 0.15;
  });
  return (
    <group position={position}>
      {/* Star core (bright, always visible) */}
      <mesh ref={ref}>
        <sphereGeometry args={[size, 12, 10]} />
        <meshBasicMaterial color={color} transparent opacity={1} toneMapped={false} />
      </mesh>
      {/* Glow halo */}
      <mesh>
        <sphereGeometry args={[size * 2.4, 12, 10]} />
        <meshBasicMaterial color={color} transparent opacity={0.25} depthWrite={false} toneMapped={false} />
      </mesh>
      {/* Faint outer flare */}
      <mesh>
        <sphereGeometry args={[size * 4.5, 12, 10]} />
        <meshBasicMaterial color={color} transparent opacity={0.08} depthWrite={false} toneMapped={false} />
      </mesh>
      {/* Label - name */}
      <Billboard position={[0, size * 2.5 + 3, 0]} follow>
        <Text
          fontSize={5.5}
          color={color}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.15}
          outlineColor="#020108"
          letterSpacing={0.15}
        >
          {name}
        </Text>
        <Text
          position={[0, -4.5, 0]}
          fontSize={2.8}
          color="#9AC5FF"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.08}
          outlineColor="#020108"
          letterSpacing={0.32}
        >
          {distance}
        </Text>
      </Billboard>
    </group>
  );
}


/* ============================================================
   Birds - Earth mode flock
   ============================================================ */
function Birds() {
  const BIRD_COUNT = 14;
  const birds = useMemo(() => {
    const arr = [];
    const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
    for (let i = 0; i < BIRD_COUNT; i++) {
      const angle = rng(i * 3.7 + 1) * Math.PI * 2;
      const radius = 45 + rng(i * 5.1 + 2) * 80;
      arr.push({
        id: i,
        radius,
        angleOffset: angle,
        speed: 0.15 + rng(i * 2.7 + 3) * 0.35,
        yBase: 12 + rng(i * 9.1 + 4) * 30,
        yAmp: 3 + rng(i * 11 + 5) * 5,
        yFreq: 0.8 + rng(i * 4 + 6) * 1.2,
        flapPhase: rng(i * 6 + 7) * Math.PI * 2,
        color: i % 4 === 0 ? "#0B0A0C" : (i % 4 === 1 ? "#2A1E22" : (i % 4 === 2 ? "#3A2A2E" : "#1E1418")),
      });
    }
    return arr;
  }, []);

  return (
    <>
      {birds.map((b) => (
        <Bird key={b.id} data={b} />
      ))}
    </>
  );
}

function Bird({ data }) {
  const groupRef = useRef();
  const leftWing = useRef();
  const rightWing = useRef();
  const registerBirdHit = useWorldStore((s) => s.registerBirdHit);
  const positionRef = useWorldStore.getState;
  const lastHit = useRef(0);

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    const a = data.angleOffset + t * data.speed;
    const x = Math.cos(a) * data.radius;
    const z = Math.sin(a) * data.radius;
    const y = data.yBase + Math.sin(t * data.yFreq + data.flapPhase) * data.yAmp;
    groupRef.current.position.set(x, y, z);
    groupRef.current.rotation.y = -a + Math.PI / 2;
    const flap = Math.sin(t * 12 + data.flapPhase) * 0.6;
    if (leftWing.current) leftWing.current.rotation.z = 0.3 + flap;
    if (rightWing.current) rightWing.current.rotation.z = -0.3 - flap;

    const st = positionRef();
    const p = st.position;
    const dx = p.x - x;
    const dy = p.y - y;
    const dz = p.z - z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d < 3.2 && t - lastHit.current > 1.5 && !st.openId) {
      lastHit.current = t;
      registerBirdHit();
    }
  });

  return (
    <group ref={groupRef}>
      <mesh>
        <sphereGeometry args={[0.4, 8, 8]} />
        <meshStandardMaterial color={data.color} roughness={0.9} />
      </mesh>
      <group ref={leftWing} position={[0.4, 0, 0]}>
        <mesh position={[0.75, 0, 0]}>
          <boxGeometry args={[1.5, 0.06, 0.6]} />
          <meshStandardMaterial color={data.color} roughness={0.9} />
        </mesh>
      </group>
      <group ref={rightWing} position={[-0.4, 0, 0]}>
        <mesh position={[-0.75, 0, 0]}>
          <boxGeometry args={[1.5, 0.06, 0.6]} />
          <meshStandardMaterial color={data.color} roughness={0.9} />
        </mesh>
      </group>
    </group>
  );
}

/* ============================================================
   Asteroids - Space-mode tumbling rocks
   ============================================================ */
function Asteroids() {
  const items = useMemo(() => {
    const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
    const arr = [];
    for (let i = 0; i < 14; i++) {
      const angle = rng(i * 3.7 + 1) * Math.PI * 2;
      const radius = 55 + rng(i * 5.1 + 2) * 200;
      arr.push({
        id: i,
        radius,
        angleOffset: angle,
        speed: 0.05 + rng(i * 2.7 + 3) * 0.2,
        yBase: -20 + rng(i * 9.1 + 4) * 80,
        yAmp: 2 + rng(i * 11 + 5) * 6,
        yFreq: 0.3 + rng(i * 4 + 6) * 0.7,
        size: 1.2 + rng(i * 6 + 7) * 2.2,
        tumbleX: rng(i * 13) * 2,
        tumbleY: rng(i * 17) * 2,
      });
    }
    return arr;
  }, []);
  return (
    <>
      {items.map((a) => (
        <Asteroid key={a.id} data={a} />
      ))}
    </>
  );
}


/* ============================================================
   Satellites - Space-mode drifting objects (replace clouds)
   ============================================================ */
function Satellites() {
  const items = useMemo(() => {
    const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
    return CLOUD_POSITIONS.map((pos, i) => ({
      pos,
      seed: i,
      speed: 0.02 + rng(i * 3) * 0.04,
      tilt: rng(i * 7) * Math.PI,
    }));
  }, []);
  return (
    <>
      {items.map((s, i) => (
        <Satellite key={i} data={s} />
      ))}
    </>
  );
}


/* ============================================================
   Parcel - dropped package with parachute physics
   ============================================================ */
function Parcels() {
  const parcels = useWorldStore((s) => s.parcels);
  return (
    <>
      {parcels.map((p) => (
        <ParcelItem key={p.id} data={p} />
      ))}
    </>
  );
}

function ParcelItem({ data }) {
  const groupRef = useRef();
  const chuteRef = useRef();
  const stringRefs = useRef([]);
  const state = useRef({
    x: data.x, y: data.y, z: data.z,
    vx: data.vx, vy: data.vy, vz: data.vz,
    chuteOpen: false,
    bornAt: data.bornAt,
    swing: 0,
  });
  const removeParcel = useWorldStore((s) => s.removeParcel);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    const s = state.current;
    const age = (performance.now() - s.bornAt) / 1000;
    // Open chute after 0.7s
    if (!s.chuteOpen && age > 0.7) {
      s.chuteOpen = true;
    }
    // Physics
    const g = 9.8;
    if (s.chuteOpen) {
      // Drag: heavy damping on vertical, keep some horizontal
      s.vy = Math.max(s.vy - g * delta * 0.15, -3.5);
      s.vx *= 1 - delta * 0.5;
      s.vz *= 1 - delta * 0.5;
      s.swing += delta;
    } else {
      s.vy -= g * delta;
    }
    s.x += s.vx * delta;
    s.y += s.vy * delta;
    s.z += s.vz * delta;
    groupRef.current.position.set(s.x, s.y, s.z);
    // Swing rotation
    groupRef.current.rotation.z = s.chuteOpen ? Math.sin(s.swing * 2) * 0.15 : 0;
    groupRef.current.rotation.x = s.chuteOpen ? Math.cos(s.swing * 1.5) * 0.1 : 0;
    // Grow chute
    if (chuteRef.current) {
      const scale = s.chuteOpen ? THREE.MathUtils.lerp(chuteRef.current.scale.x, 1, delta * 6) : 0.001;
      chuteRef.current.scale.setScalar(scale);
      chuteRef.current.visible = s.chuteOpen;
    }
    // Remove if hit water/ground or too old
    if (s.y < 0.5 || age > 25) {
      removeParcel(data.id);
    }
  });

  return (
    <group ref={groupRef} position={[data.x, data.y, data.z]}>
      {/* Box */}
      <mesh>
        <boxGeometry args={[0.9, 0.7, 0.9]} />
        <meshStandardMaterial color="#B3641C" roughness={0.9} />
      </mesh>
      {/* Tape stripes */}
      <mesh position={[0, 0.36, 0]}>
        <boxGeometry args={[0.92, 0.03, 0.25]} />
        <meshStandardMaterial color="#EFE3D6" />
      </mesh>
      <mesh position={[0, 0.36, 0]} rotation={[0, Math.PI / 2, 0]}>
        <boxGeometry args={[0.92, 0.03, 0.25]} />
        <meshStandardMaterial color="#EFE3D6" />
      </mesh>
      {/* GREINCH label */}
      <mesh position={[0, 0, 0.46]}>
        <planeGeometry args={[0.5, 0.25]} />
        <meshStandardMaterial color="#E94B4B" emissive="#E94B4B" emissiveIntensity={0.4} />
      </mesh>
      {/* Parachute - dome canvas */}
      <group ref={chuteRef} position={[0, 2.4, 0]} scale={0.001}>
        <mesh>
          <sphereGeometry args={[1.4, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#E94B4B" side={THREE.DoubleSide} roughness={0.7} />
        </mesh>
        {/* Alt-color panels */}
        <mesh rotation={[0, Math.PI / 4, 0]}>
          <sphereGeometry args={[1.4, 16, 12, 0, Math.PI / 4, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#F4EADE" side={THREE.DoubleSide} roughness={0.7} />
        </mesh>
        <mesh rotation={[0, (3 * Math.PI) / 4, 0]}>
          <sphereGeometry args={[1.4, 16, 12, 0, Math.PI / 4, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#F4EADE" side={THREE.DoubleSide} roughness={0.7} />
        </mesh>
        {/* Strings */}
        {[
          [1.3, 0], [-1.3, 0], [0, 1.3], [0, -1.3],
        ].map((s, i) => (
          <mesh key={i} ref={(el) => (stringRefs.current[i] = el)} position={[s[0] / 2, -1.2, s[1] / 2]} rotation={[0, 0, Math.atan2(-s[0], -2.4)]}>
            <cylinderGeometry args={[0.02, 0.02, 2.4, 4]} />
            <meshStandardMaterial color="#EFE3D6" />
          </mesh>
        ))}
      </group>
    </group>
  );
}

/* ============================================================
   Player Lasers - our ship's laser bolts (fired in space mode)
   ============================================================ */
function PlayerLasers() {
  const lasers = useWorldStore((s) => s.playerLasers);
  return (
    <>
      {lasers.map((l) => (
        <PlayerLaserBolt key={l.id} data={l} />
      ))}
    </>
  );
}

function PlayerLaserBolt({ data }) {
  const ref = useRef();
  const state = useRef({
    x: data.x, y: data.y, z: data.z,
    vx: data.vx, vy: data.vy, vz: data.vz,
    bornAt: data.bornAt,
  });
  const removePlayerLaser = useWorldStore((s) => s.removePlayerLaser);

  useFrame((_, delta) => {
    if (!ref.current) return;
    const s = state.current;
    const age = (performance.now() - s.bornAt) / 1000;
    if (age > 2.5) {
      removePlayerLaser(data.id);
      return;
    }
    s.x += s.vx * delta;
    s.y += s.vy * delta;
    s.z += s.vz * delta;
    ref.current.position.set(s.x, s.y, s.z);
    ref.current.lookAt(s.x + s.vx, s.y + s.vy, s.z + s.vz);
    const alpha = Math.max(0, 1 - age / 2.5);
    ref.current.children.forEach((c) => {
      if (c.material) c.material.opacity = alpha;
    });
  });

  return (
    <group ref={ref} position={[data.x, data.y, data.z]}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.14, 0.14, 3.5, 8]} />
        <meshBasicMaterial color="#5A99CC" transparent opacity={1} toneMapped={false} depthWrite={false} />
      </mesh>
      <mesh position={[0, 0, -1.8]}>
        <sphereGeometry args={[0.32, 12, 10]} />
        <meshBasicMaterial color="#FFFFFF" transparent opacity={1} toneMapped={false} />
      </mesh>
    </group>
  );
}

/* ============================================================
   Golden Gate style bridge between two islands
   ============================================================ */
function GoldenGateBridge() {
  // Connect the two closest airports: services & contact
  const bridgeInfo = useMemo(() => {
    const A = AIRPORTS.find((a) => a.id === "services").position;
    const B = AIRPORTS.find((a) => a.id === "contact").position;
    const dx = B[0] - A[0];
    const dz = B[2] - A[2];
    const dist = Math.sqrt(dx * dx + dz * dz);
    const ux = dx / dist;
    const uz = dz / dist;
    // Extend deck DEEP into each island (only 22 from center - overlaps grass at 30)
    // so the deck visually merges into the terrain
    const startX = A[0] + ux * 22;
    const startZ = A[2] + uz * 22;
    const endX = B[0] - ux * 22;
    const endZ = B[2] - uz * 22;
    const midX = (startX + endX) / 2;
    const midZ = (startZ + endZ) / 2;
    const span = Math.sqrt((endX - startX) ** 2 + (endZ - startZ) ** 2);
    // yaw: rotate so local +Z aligns with bridge direction (from start to end)
    const angle = Math.atan2(dx, dz);
    return { midX, midZ, span, angle };
  }, []);

  // Suspension cable curve points (catenary parabola)
  const towerZ = bridgeInfo.span * 0.4;
  const towerTop = 24;
  const deckLow = 4;
  const midSag = 6;
  const cableCurve = useMemo(() => {
    const pts = [];
    const N = 24;
    // From -towerZ (top) → sag at middle → +towerZ (top), then extend to deck ends
    // Full span: -span/2 to +span/2 in local Z
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const z = (t - 0.5) * bridgeInfo.span;
      if (z <= -towerZ) {
        // Ramp from deck to tower top
        const r = (z + bridgeInfo.span / 2) / (bridgeInfo.span / 2 - towerZ);
        pts.push(new THREE.Vector3(0, deckLow + r * (towerTop - deckLow), z));
      } else if (z >= towerZ) {
        // Ramp from tower top to deck
        const r = (bridgeInfo.span / 2 - z) / (bridgeInfo.span / 2 - towerZ);
        pts.push(new THREE.Vector3(0, deckLow + r * (towerTop - deckLow), z));
      } else {
        // Between towers - parabola sagging in the middle
        const localT = (z + towerZ) / (2 * towerZ); // 0..1
        const parabola = 1 - Math.pow(2 * localT - 1, 2); // 0 at ends, 1 at middle
        pts.push(new THREE.Vector3(0, towerTop - (towerTop - midSag) * parabola, z));
      }
    }
    return pts;
  }, [bridgeInfo.span]);

  return (
    <group position={[bridgeInfo.midX, 0, bridgeInfo.midZ]} rotation={[0, bridgeInfo.angle, 0]}>
      {/* Deck (roadway) */}
      <mesh position={[0, deckLow, 0]}>
        <boxGeometry args={[6, 0.5, bridgeInfo.span]} />
        <meshStandardMaterial color="#2A2622" roughness={0.9} />
      </mesh>
      {/* Deck top surface (asphalt) */}
      <mesh position={[0, deckLow + 0.26, 0]}>
        <boxGeometry args={[5.6, 0.05, bridgeInfo.span]} />
        <meshStandardMaterial color="#3A3238" roughness={0.95} />
      </mesh>
      {/* Deck center yellow line */}
      <mesh position={[0, deckLow + 0.29, 0]}>
        <boxGeometry args={[0.15, 0.03, bridgeInfo.span]} />
        <meshStandardMaterial color="#F0A020" emissive="#F0A020" emissiveIntensity={0.4} />
      </mesh>
      {/* Deck edges/rails - Golden Gate signature "International Orange" */}
      <mesh position={[2.8, deckLow + 0.5, 0]}>
        <boxGeometry args={[0.25, 0.7, bridgeInfo.span]} />
        <meshStandardMaterial color="#C93B24" emissive="#C93B24" emissiveIntensity={0.35} />
      </mesh>
      <mesh position={[-2.8, deckLow + 0.5, 0]}>
        <boxGeometry args={[0.25, 0.7, bridgeInfo.span]} />
        <meshStandardMaterial color="#C93B24" emissive="#C93B24" emissiveIntensity={0.35} />
      </mesh>

      {/* Twin towers */}
      {[-1, 1].map((s, i) => (
        <group key={`t-${i}`} position={[0, 0, s * towerZ]}>
          {/* Two vertical pillars (each tower) */}
          {[-2.6, 2.6].map((sx, j) => (
            <mesh key={j} position={[sx, towerTop / 2, 0]}>
              <boxGeometry args={[1.1, towerTop, 1.1]} />
              <meshStandardMaterial color="#C93B24" emissive="#C93B24" emissiveIntensity={0.4} />
            </mesh>
          ))}
          {/* Art-deco horizontal crossbeams (3 levels) */}
          <mesh position={[0, 8, 0]}>
            <boxGeometry args={[6.5, 0.5, 0.7]} />
            <meshStandardMaterial color="#B0311C" />
          </mesh>
          <mesh position={[0, 15, 0]}>
            <boxGeometry args={[6.5, 0.5, 0.7]} />
            <meshStandardMaterial color="#B0311C" />
          </mesh>
          <mesh position={[0, towerTop - 0.5, 0]}>
            <boxGeometry args={[7.2, 0.5, 1.6]} />
            <meshStandardMaterial color="#B0311C" />
          </mesh>
          {/* Top decorative cap */}
          <mesh position={[0, towerTop + 0.5, 0]}>
            <boxGeometry args={[5.8, 0.6, 0.9]} />
            <meshStandardMaterial color="#C93B24" />
          </mesh>
          {/* Aircraft warning light on top */}
          <mesh position={[0, towerTop + 1.1, 0]}>
            <sphereGeometry args={[0.3, 10, 8]} />
            <meshBasicMaterial color="#F0A020" toneMapped={false} />
          </mesh>
          <pointLight color="#F0A020" intensity={1.4} distance={15} position={[0, towerTop + 1, 0]} />
        </group>
      ))}

      {/* Two main suspension cables (one per side) */}
      {[-1, 1].map((sx, i) => (
        <mesh key={`cable-${i}`} position={[sx * 2.6, 0, 0]}>
          <tubeGeometry
            args={[new THREE.CatmullRomCurve3(cableCurve), 48, 0.09, 6, false]}
          />
          <meshStandardMaterial color="#C93B24" emissive="#C93B24" emissiveIntensity={0.3} />
        </mesh>
      ))}

      {/* Vertical hangers (short cables from main cable down to deck) */}
      {[-1, 1].map((sx, si) => (
        [...Array(11)].map((_, i) => {
          const localT = (i + 1) / 12; // 0..1 between towers
          const z = (localT - 0.5) * (2 * towerZ);
          const parabola = 1 - Math.pow(2 * localT - 1, 2);
          const cableY = towerTop - (towerTop - midSag) * parabola;
          const hangerLen = cableY - deckLow - 0.4;
          if (hangerLen < 0.5) return null;
          return (
            <mesh key={`h-${si}-${i}`} position={[sx * 2.6, deckLow + 0.4 + hangerLen / 2, z]}>
              <cylinderGeometry args={[0.04, 0.04, hangerLen, 4]} />
              <meshStandardMaterial color="#B0311C" />
            </mesh>
          );
        })
      ))}

      {/* Support pillars from water up to deck (below deck) - underwater supports at towers */}
      {[-1, 1].map((s, i) => (
        <mesh key={`sup-${i}`} position={[0, deckLow / 2 - 1, s * towerZ]}>
          <boxGeometry args={[7, deckLow + 4, 4]} />
          <meshStandardMaterial color="#251E18" roughness={1} />
        </mesh>
      ))}

      {/* Approach ramps at each end - visually merge into the islands */}
      {[-1, 1].map((s, i) => (
        <group key={`ramp-${i}`} position={[0, 0, s * (bridgeInfo.span / 2)]}>
          {/* Wide stone anchor block extending into island (matches island grass at y~1) */}
          <mesh position={[0, 2.5, s * 5]}>
            <boxGeometry args={[8, 7, 12]} />
            <meshStandardMaterial color="#4A4038" roughness={1} />
          </mesh>
          {/* Ramp deck from bridge deck down to grass level */}
          <mesh position={[0, deckLow - 0.5, s * 5]} rotation={[s * 0.28, 0, 0]}>
            <boxGeometry args={[6, 0.5, 10]} />
            <meshStandardMaterial color="#2A2622" roughness={0.9} />
          </mesh>
          {/* Ramp asphalt */}
          <mesh position={[0, deckLow - 0.25, s * 5]} rotation={[s * 0.28, 0, 0]}>
            <boxGeometry args={[5.6, 0.06, 10]} />
            <meshStandardMaterial color="#3A3238" roughness={0.95} />
          </mesh>
          {/* Ramp rails */}
          <mesh position={[2.8, deckLow, s * 5]} rotation={[s * 0.28, 0, 0]}>
            <boxGeometry args={[0.25, 0.6, 10]} />
            <meshStandardMaterial color="#C93B24" emissive="#C93B24" emissiveIntensity={0.3} />
          </mesh>
          <mesh position={[-2.8, deckLow, s * 5]} rotation={[s * 0.28, 0, 0]}>
            <boxGeometry args={[0.25, 0.6, 10]} />
            <meshStandardMaterial color="#C93B24" emissive="#C93B24" emissiveIntensity={0.3} />
          </mesh>
        </group>
      ))}

      {/* Cars driving in both lanes across the bridge */}
      <BridgeCars span={bridgeInfo.span} />
    </group>
  );
}

/* Cars on the bridge deck - two lanes with looping traffic */
function BridgeCars({ span }) {
  const cars = useMemo(() => {
    const palette = ["#E94B4B", "#3A6BA5", "#F0A020", "#2E8B57", "#EFE3D6", "#8B5CF6", "#B34A3A", "#22D3B4"];
    const N_PER_LANE = 7;
    const arr = [];
    for (let i = 0; i < N_PER_LANE; i++) {
      const zRatio = i / N_PER_LANE;
      arr.push({
        lane: 1,   // right lane, moving +Z
        z0: zRatio * span - span / 2,
        speed: 7 + (i % 3) * 1.5,
        color: palette[i % palette.length],
      });
    }
    for (let i = 0; i < N_PER_LANE; i++) {
      const zRatio = i / N_PER_LANE;
      arr.push({
        lane: -1,  // left lane, moving -Z
        z0: zRatio * span - span / 2,
        speed: -(7 + (i % 3) * 1.5),
        color: palette[(i + 3) % palette.length],
      });
    }
    return arr;
  }, [span]);
  return (
    <>
      {cars.map((c, i) => (
        <BridgeCar key={i} data={c} span={span} />
      ))}
    </>
  );
}

function BridgeCar({ data, span }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    // Loop position along Z (bridge local axis)
    let z = data.z0 + data.speed * t;
    z = ((z + span / 2) % span + span) % span - span / 2;
    ref.current.position.set(data.lane * 1.3, 4.55, z);
    ref.current.rotation.y = data.lane === 1 ? 0 : Math.PI;
  });
  return (
    <group ref={ref}>
      {/* Car body */}
      <mesh>
        <boxGeometry args={[0.85, 0.4, 1.9]} />
        <meshStandardMaterial color={data.color} metalness={0.5} roughness={0.4} />
      </mesh>
      {/* Cabin (roof) */}
      <mesh position={[0, 0.34, 0]}>
        <boxGeometry args={[0.8, 0.32, 1.2]} />
        <meshStandardMaterial color={data.color} metalness={0.5} roughness={0.45} />
      </mesh>
      {/* Windshield (front) */}
      <mesh position={[0, 0.28, 0.65]} rotation={[0.35, 0, 0]}>
        <boxGeometry args={[0.72, 0.28, 0.42]} />
        <meshStandardMaterial color="#0F0D11" metalness={0.85} roughness={0.1} emissive="#5A99CC" emissiveIntensity={0.15} />
      </mesh>
      {/* Rear window */}
      <mesh position={[0, 0.28, -0.65]} rotation={[-0.35, 0, 0]}>
        <boxGeometry args={[0.72, 0.26, 0.35]} />
        <meshStandardMaterial color="#0F0D11" metalness={0.85} roughness={0.1} />
      </mesh>
      {/* Side windows */}
      <mesh position={[0.4, 0.32, 0]}>
        <boxGeometry args={[0.02, 0.2, 0.9]} />
        <meshStandardMaterial color="#0F0D11" metalness={0.85} />
      </mesh>
      <mesh position={[-0.4, 0.32, 0]}>
        <boxGeometry args={[0.02, 0.2, 0.9]} />
        <meshStandardMaterial color="#0F0D11" metalness={0.85} />
      </mesh>
      {/* Headlights (front - +Z when lane==1) */}
      <mesh position={[0.28, -0.05, 0.96]}>
        <sphereGeometry args={[0.09, 6, 6]} />
        <meshBasicMaterial color="#FFF9C8" toneMapped={false} />
      </mesh>
      <mesh position={[-0.28, -0.05, 0.96]}>
        <sphereGeometry args={[0.09, 6, 6]} />
        <meshBasicMaterial color="#FFF9C8" toneMapped={false} />
      </mesh>
      {/* Taillights */}
      <mesh position={[0.28, -0.05, -0.96]}>
        <sphereGeometry args={[0.08, 6, 6]} />
        <meshBasicMaterial color="#E94B4B" toneMapped={false} />
      </mesh>
      <mesh position={[-0.28, -0.05, -0.96]}>
        <sphereGeometry args={[0.08, 6, 6]} />
        <meshBasicMaterial color="#E94B4B" toneMapped={false} />
      </mesh>
      {/* Wheels */}
      {[
        [0.42, -0.2, 0.6],
        [-0.42, -0.2, 0.6],
        [0.42, -0.2, -0.6],
        [-0.42, -0.2, -0.6],
      ].map((p, i) => (
        <mesh key={i} position={p} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.15, 0.15, 0.12, 10]} />
          <meshStandardMaterial color="#1A1A22" roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
}


// Earth planet world position exposed for proximity check
const EARTH_PLANET_POS = [-30, 20, 240];

/* ============================================================
   Root Scene component - Canvas wrapper + scene composition
   ============================================================ */
function EarthWorld() {
  return (
    <>
      <Sky />
      <ambientLight intensity={0.55} color="#EAF3FF" />
      <directionalLight color="#FFE9C6" intensity={1.4} position={SUN_WORLD_POS} castShadow={false} />
      <hemisphereLight args={["#B7D8EF", "#183048", 0.7]} />
      <Ocean />
      <CloudShadows />
      <Scenery />
      <Whale />
      <Birds />
      <GoldenGateBridge />
      {AIRPORTS.map((ap) => (
        <Airport key={ap.id} airport={ap} />
      ))}
    </>
  );
}

function SpaceWorld() {
  return (
    <>
      <SpaceSky />
      <ambientLight intensity={0.35} color="#9AC5FF" />
      <directionalLight color="#C5A8FF" intensity={1.1} position={[600, 100, -700]} castShadow={false} />
      <hemisphereLight args={["#1A2A55", "#0B0620", 0.4]} />
      <Asteroids />
      <Satellites />
      <AlienShip />
      <PlayerLasers />
      <EarthPlanet />
      {AIRPORTS.map((ap, i) => (
        <Planet key={ap.id} airport={ap} index={i} />
      ))}
    </>
  );
}

/* ============================================================
   UnderwaterWorld - deep ocean sub-world with coral / fish / nets / shark
   ============================================================ */
function UnderwaterSky() {
  return (
    <>
      <color attach="background" args={["#0A3560"]} />
      <fog attach="fog" args={["#134E75", 30, 320]} />
      {/* Sun rays from above (bright surface plane) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 25, 0]}>
        <planeGeometry args={[600, 600]} />
        <meshBasicMaterial color="#5A99CC" transparent opacity={0.4} depthWrite={false} />
      </mesh>
    </>
  );
}

function CoralPatch({ data }) {
  const colors = ["#E94B4B", "#F0A020", "#8B5CF6", "#22D3B4", "#FFB07A", "#F4C4A0"];
  const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
  const color = colors[Math.floor(rng(data.seed * 13) * colors.length)];
  const color2 = colors[Math.floor(rng(data.seed * 17 + 3) * colors.length)];
  return (
    <group position={[data.x, -30, data.z]} scale={data.scale}>
      {/* Base rock */}
      <mesh position={[0, 0.2, 0]} scale={[1.2, 0.5, 1.2]}>
        <sphereGeometry args={[1.5, 10, 8]} />
        <meshStandardMaterial color="#5A4A48" roughness={0.95} />
      </mesh>
      {/* Branching coral formations */}
      {[...Array(6)].map((_, i) => {
        const a = i * 1.05 + data.seed;
        const bx = Math.cos(a) * 0.9;
        const bz = Math.sin(a) * 0.9;
        const h = 1.2 + (i % 3) * 0.4;
        const useAlt = i % 2 === 0;
        return (
          <mesh key={i} position={[bx, 0.6 + h / 2, bz]}>
            <coneGeometry args={[0.5 - (i % 3) * 0.1, h, 6]} />
            <meshStandardMaterial color={useAlt ? color : color2} roughness={0.85} emissive={useAlt ? color : color2} emissiveIntensity={0.2} />
          </mesh>
        );
      })}
      {/* Anemone tendrils on top */}
      {[...Array(5)].map((_, i) => {
        const a = i * 1.3 + data.seed * 2;
        return (
          <mesh key={`t-${i}`} position={[Math.cos(a) * 0.5, 2.2, Math.sin(a) * 0.5]}>
            <cylinderGeometry args={[0.05, 0.08, 0.8, 4]} />
            <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.4} />
          </mesh>
        );
      })}
    </group>
  );
}

/* ============================================================
   Underwater terrain heightmap data - shared between GeoTerrain
   (vertex displacement) and decoration placement
   ============================================================ */
const CANYON_DATA = [
  { x1: -20, z1: -20, x2: 130, z2: 130, width: 22, depth: 32 },
  { x1: -220, z1: -140, x2: -60, z2: -20, width: 26, depth: 40 },
  { x1: 240, z1: -140, x2: 140, z2: -240, width: 20, depth: 26 },
];
const SEAMOUNT_DATA = [
  { x: -80, z: -220, radius: 44, height: 30, seed: 3 },
  { x: 240, z: 40, radius: 52, height: 38, seed: 7 },
  { x: -260, z: 180, radius: 48, height: 34, seed: 11 },
  { x: 90, z: -260, radius: 40, height: 26, seed: 13 },
  { x: -30, z: -100, radius: 34, height: 20, seed: 17 },
  { x: 180, z: 130, radius: 38, height: 24, seed: 19 },
];

function CoralFloor() {
  const corals = useMemo(() => {
    const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
    const distToCanyon = (px, pz) => {
      let best = Infinity;
      for (const c of CANYON_DATA) {
        const vx = c.x2 - c.x1;
        const vz = c.z2 - c.z1;
        const lenSq = vx * vx + vz * vz;
        const t = Math.max(0, Math.min(1, ((px - c.x1) * vx + (pz - c.z1) * vz) / lenSq));
        const nx = c.x1 + t * vx;
        const nz = c.z1 + t * vz;
        const d = Math.sqrt((px - nx) * (px - nx) + (pz - nz) * (pz - nz)) - c.width;
        if (d < best) best = d;
      }
      return best;
    };
    const arr = [];
    let attempts = 0;
    while (arr.length < 40 && attempts < 200) {
      const i = attempts;
      const a = rng(i * 3.1 + 1) * Math.PI * 2;
      const r = 25 + rng(i * 5.7 + 2) * 180;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      attempts++;
      if (distToCanyon(x, z) < 6) continue; // no corals inside canyons
      arr.push({ x, z, seed: i, scale: 0.8 + rng(i * 7) * 0.9 });
    }
    return arr;
  }, []);
  return (
    <group>
      <GeoTerrain />
      {corals.map((c, i) => (
        <CoralPatch key={i} data={c} />
      ))}
    </group>
  );
}

/* Real 3D seafloor terrain with canyons carved and seamounts pushed up.
   Uses PlaneGeometry vertex displacement + per-vertex colors for realistic gradient.
   Geometry is computed ONCE at module scope so world switches don't re-run this. */
const TERRAIN_GEOMETRY = (() => {
  const size = 900;
  const segs = 130; // ~17k verts — smooth enough, 4x faster than 220
  const geo = new THREE.PlaneGeometry(size, size, segs, segs);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  const colors = new Float32Array(pos.count * 3);

  const noise = (x, z) => {
    const a = Math.sin(x * 0.13 + z * 0.09) * 0.5;
    const b = Math.sin(x * 0.31 - z * 0.27) * 0.25;
    const c = Math.sin(x * 0.58 + z * 0.49) * 0.12;
    return a + b + c;
  };

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    let y = noise(x, z) * 1.6;

    for (const m of SEAMOUNT_DATA) {
      const dx = x - m.x;
      const dz = z - m.z;
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d < m.radius) {
        const t = 1 - d / m.radius;
        const bump = Math.pow(t, 1.8) * m.height;
        const rockNoise = noise(x * 1.5 + m.seed, z * 1.5 + m.seed) * t * 4;
        y += bump + rockNoise;
      }
    }

    for (const c of CANYON_DATA) {
      const vx = c.x2 - c.x1;
      const vz = c.z2 - c.z1;
      const lenSq = vx * vx + vz * vz;
      const t = Math.max(0, Math.min(1, ((x - c.x1) * vx + (z - c.z1) * vz) / lenSq));
      const nx = c.x1 + t * vx;
      const nz = c.z1 + t * vz;
      const d = Math.sqrt((x - nx) * (x - nx) + (z - nz) * (z - nz));
      if (d < c.width) {
        const norm = d / c.width;
        const carve = c.depth * Math.pow(1 - norm, 1.5);
        const wallNoise = noise(x * 0.8, z * 0.8) * 2.5 * (1 - norm * 0.5);
        y -= carve + wallNoise;
        const endFade = Math.min(t * 8, (1 - t) * 8, 1);
        if (endFade < 1) y += (carve + wallNoise) * (1 - endFade);
      }
    }

    if (y < -46) y = -46 - (y + 46) * 0.05;
    pos.setY(i, y);

    const idx = i * 3;
    if (y > 22) {
      colors[idx] = 0.22; colors[idx + 1] = 0.16; colors[idx + 2] = 0.11;
    } else if (y > 10) {
      colors[idx] = 0.32; colors[idx + 1] = 0.24; colors[idx + 2] = 0.15;
    } else if (y > 2) {
      colors[idx] = 0.42; colors[idx + 1] = 0.32; colors[idx + 2] = 0.20;
    } else if (y > -2) {
      colors[idx] = 0.55; colors[idx + 1] = 0.43; colors[idx + 2] = 0.26;
    } else if (y > -12) {
      const t = (y + 12) / 10;
      colors[idx] = 0.34 * t + 0.15 * (1 - t);
      colors[idx + 1] = 0.24 * t + 0.10 * (1 - t);
      colors[idx + 2] = 0.14 * t + 0.06 * (1 - t);
    } else if (y > -28) {
      colors[idx] = 0.11; colors[idx + 1] = 0.08; colors[idx + 2] = 0.05;
    } else {
      colors[idx] = 0.03; colors[idx + 1] = 0.03; colors[idx + 2] = 0.05;
    }
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();
  return geo;
})();

function GeoTerrain() {
  return (
    <mesh geometry={TERRAIN_GEOMETRY} position={[0, -30, 0]} receiveShadow>
      <meshStandardMaterial vertexColors roughness={1} flatShading={false} />
    </mesh>
  );
}

function UwFish({ data }) {
  const ref = useRef();
  const bodyRef = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    const a = data.angleOffset + t * data.speed;
    const x = Math.cos(a) * data.radius;
    const z = Math.sin(a) * data.radius;
    const y = data.yBase + Math.sin(t * data.yFreq + data.flapPhase) * data.yAmp;
    ref.current.position.set(x, y, z);
    ref.current.rotation.y = -a + Math.PI / 2;
    if (bodyRef.current) bodyRef.current.rotation.y = Math.sin(t * 6 + data.flapPhase) * 0.25;
  });
  return (
    <group ref={ref}>
      <group ref={bodyRef}>
        {/* Fish body */}
        <mesh scale={[1.8, 0.7, 0.6]}>
          <sphereGeometry args={[0.35, 12, 8]} />
          <meshStandardMaterial color={data.color} roughness={0.55} metalness={0.15} />
        </mesh>
        {/* Belly lighter */}
        <mesh position={[0, -0.13, 0]} scale={[1.7, 0.35, 0.55]}>
          <sphereGeometry args={[0.35, 10, 6]} />
          <meshStandardMaterial color="#F4EADE" roughness={0.75} />
        </mesh>
        {/* Tail */}
        <mesh position={[-0.7, 0, 0]}>
          <coneGeometry args={[0.28, 0.45, 4]} />
          <meshStandardMaterial color={data.color} roughness={0.6} />
        </mesh>
        <mesh position={[-0.85, 0.18, 0]} rotation={[0, 0, 0.4]}>
          <boxGeometry args={[0.4, 0.05, 0.35]} />
          <meshStandardMaterial color={data.color} />
        </mesh>
        <mesh position={[-0.85, -0.18, 0]} rotation={[0, 0, -0.4]}>
          <boxGeometry args={[0.4, 0.05, 0.35]} />
          <meshStandardMaterial color={data.color} />
        </mesh>
        {/* Dorsal fin */}
        <mesh position={[0, 0.28, 0]}>
          <coneGeometry args={[0.2, 0.35, 3]} />
          <meshStandardMaterial color={data.color} />
        </mesh>
        {/* Eye */}
        <mesh position={[0.5, 0.1, 0.22]}>
          <sphereGeometry args={[0.06, 6, 6]} />
          <meshStandardMaterial color="#F4EADE" />
        </mesh>
        <mesh position={[0.52, 0.1, 0.24]}>
          <sphereGeometry args={[0.035, 6, 6]} />
          <meshStandardMaterial color="#0B0A0C" />
        </mesh>
      </group>
    </group>
  );
}

function FishSchool() {
  const fishes = useMemo(() => {
    const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
    const colors = ["#F0A020", "#22D3B4", "#8B5CF6", "#E94B4B", "#5A99CC", "#F4C4A0", "#FFB07A"];
    const arr = [];
    for (let i = 0; i < 14; i++) {
      arr.push({
        id: i,
        radius: 25 + rng(i * 5.1) * 150,
        angleOffset: rng(i * 3.7) * Math.PI * 2,
        speed: 0.25 + rng(i * 2.7) * 0.5,
        yBase: -20 + rng(i * 9.1) * 20,
        yAmp: 1 + rng(i * 11) * 3,
        yFreq: 0.6 + rng(i * 4) * 1.5,
        flapPhase: rng(i * 6) * Math.PI * 2,
        color: colors[i % colors.length],
      });
    }
    return arr;
  }, []);
  return <>{fishes.map((f) => <UwFish key={f.id} data={f} />)}</>;
}

function FishingNet({ data }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    ref.current.rotation.y = t * data.speed + data.seed;
    ref.current.position.x = data.pos[0] + Math.sin(t * 0.1 + data.seed) * 4;
    ref.current.position.y = data.pos[1] + Math.cos(t * 0.15 + data.seed) * 2;
    ref.current.position.z = data.pos[2] + Math.cos(t * 0.12 + data.seed) * 3;
  });
  return (
    <group ref={ref}>
      {/* Net cage frame */}
      {[[0, 1, 0], [0, -1, 0], [1, 0, 0], [-1, 0, 0], [0, 0, 1], [0, 0, -1]].map((p, i) => (
        <mesh key={i} position={p} scale={[2, 0.05, 2]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial color="#B08830" wireframe transparent opacity={0.6} />
        </mesh>
      ))}
      {/* Rope corners */}
      {[[-1, -1, -1], [1, -1, -1], [-1, 1, -1], [1, 1, -1], [-1, -1, 1], [1, -1, 1], [-1, 1, 1], [1, 1, 1]].map((p, i) => (
        <mesh key={`c-${i}`} position={p}>
          <sphereGeometry args={[0.12, 6, 6]} />
          <meshStandardMaterial color="#8A6540" />
        </mesh>
      ))}
      {/* Trapped fish inside */}
      <mesh position={[0, 0, 0]} scale={[1.2, 0.5, 0.4]}>
        <sphereGeometry args={[0.4, 8, 6]} />
        <meshStandardMaterial color="#F0A020" roughness={0.6} />
      </mesh>
      {/* Buoys on top rope */}
      {[[-1.2, 0], [1.2, 0]].map(([lx, lz], i) => (
        <mesh key={`b-${i}`} position={[lx, 2.2, lz]}>
          <sphereGeometry args={[0.35, 10, 8]} />
          <meshStandardMaterial color="#E94B4B" emissive="#E94B4B" emissiveIntensity={0.3} />
        </mesh>
      ))}
    </group>
  );
}

function FishingNets() {
  const items = useMemo(() => {
    const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
    const arr = [];
    for (let i = 0; i < 8; i++) {
      const a = rng(i * 3.3) * Math.PI * 2;
      const r = 35 + rng(i * 5.7) * 140;
      arr.push({
        pos: [Math.cos(a) * r, -8 + rng(i * 7.3) * 15, Math.sin(a) * r],
        seed: i * 1.7,
        speed: 0.04 + rng(i * 11) * 0.08,
      });
    }
    return arr;
  }, []);
  return <>{items.map((it, i) => <FishingNet key={i} data={it} />)}</>;
}

function Shark() {
  const ref = useRef();
  const tailRef = useRef();
  const positionRef = useRef(new THREE.Vector3(80, -12, -80));
  const registerBirdHit = useWorldStore((s) => s.registerBirdHit);
  const lastHit = useRef(0);

  useFrame((state, delta) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    const st = useWorldStore.getState();
    if (!st.underwaterMode || st.openId) {
      // When not underwater, park the shark far below to be invisible
      ref.current.position.y = -60;
      return;
    }
    // Chase player at moderate speed with lag
    const dx = st.position.x - positionRef.current.x;
    const dy = st.position.y - positionRef.current.y;
    const dz = st.position.z - positionRef.current.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const speed = 5.5;
    if (dist > 1) {
      positionRef.current.x += (dx / dist) * speed * delta;
      positionRef.current.y += (dy / dist) * speed * delta;
      positionRef.current.z += (dz / dist) * speed * delta;
    }
    ref.current.position.copy(positionRef.current);
    ref.current.rotation.y = Math.atan2(dx, dz);
    // Tail wobble
    if (tailRef.current) tailRef.current.rotation.y = Math.sin(t * 6) * 0.6;
    // Bite check (proximity)
    if (dist < 3.5 && t - lastHit.current > 1.5) {
      lastHit.current = t;
      registerBirdHit();
    }
  });
  return (
    <group ref={ref} position={[80, -12, -80]}>
      {/* Body (elongated ellipsoid) */}
      <mesh scale={[0.9, 0.8, 3]}>
        <sphereGeometry args={[1.3, 16, 12]} />
        <meshStandardMaterial color="#5A6A78" roughness={0.7} />
      </mesh>
      {/* Belly lighter */}
      <mesh position={[0, -0.5, 0]} scale={[0.85, 0.35, 2.8]}>
        <sphereGeometry args={[1.3, 14, 10]} />
        <meshStandardMaterial color="#EFE3D6" roughness={0.75} />
      </mesh>
      {/* Snout (front-facing = negative Z since we rotate to look at player with atan2 dx dz) */}
      <mesh position={[0, 0, -3.5]} scale={[0.7, 0.55, 1.5]}>
        <sphereGeometry args={[1, 10, 8]} />
        <meshStandardMaterial color="#5A6A78" roughness={0.7} />
      </mesh>
      {/* Mouth - dark curve on underside of snout */}
      <mesh position={[0, -0.25, -4]}>
        <boxGeometry args={[1, 0.15, 0.9]} />
        <meshStandardMaterial color="#0B0A0C" />
      </mesh>
      {/* Teeth */}
      {[-0.35, -0.12, 0.12, 0.35].map((xo, i) => (
        <mesh key={`tooth-${i}`} position={[xo, -0.17, -4.2]}>
          <coneGeometry args={[0.07, 0.22, 3]} />
          <meshBasicMaterial color="#FFFFFF" />
        </mesh>
      ))}
      {/* Eyes (small black dots) */}
      <mesh position={[0.5, 0.3, -3]}>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial color="#0B0A0C" roughness={0.2} />
      </mesh>
      <mesh position={[-0.5, 0.3, -3]}>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial color="#0B0A0C" roughness={0.2} />
      </mesh>
      {/* Dorsal fin (large iconic triangle on top) */}
      <mesh position={[0, 1.5, 0]}>
        <coneGeometry args={[0.7, 1.9, 3]} />
        <meshStandardMaterial color="#4A5A68" roughness={0.75} />
      </mesh>
      {/* Pectoral fins */}
      <mesh position={[1.2, -0.5, -0.5]} rotation={[0, 0, -0.4]} scale={[2, 0.15, 0.85]}>
        <sphereGeometry args={[1, 10, 8]} />
        <meshStandardMaterial color="#5A6A78" />
      </mesh>
      <mesh position={[-1.2, -0.5, -0.5]} rotation={[0, 0, 0.4]} scale={[2, 0.15, 0.85]}>
        <sphereGeometry args={[1, 10, 8]} />
        <meshStandardMaterial color="#5A6A78" />
      </mesh>
      {/* Tail with wobble */}
      <group ref={tailRef} position={[0, 0, 3]}>
        <mesh scale={[0.35, 0.4, 1.2]}>
          <sphereGeometry args={[1, 8, 6]} />
          <meshStandardMaterial color="#5A6A78" />
        </mesh>
        {/* Vertical caudal fin (shark's signature Y-shape) */}
        <mesh position={[0, 0.9, 1]}>
          <coneGeometry args={[0.7, 1.7, 3]} />
          <meshStandardMaterial color="#4A5A68" />
        </mesh>
        <mesh position={[0, -0.5, 1]}>
          <coneGeometry args={[0.45, 1.1, 3]} rotation={[Math.PI, 0, 0]} />
          <meshStandardMaterial color="#4A5A68" />
        </mesh>
      </group>
      <pointLight color="#5A6A78" intensity={0.6} distance={12} />
    </group>
  );
}

function Bubbles() {
  const bubbles = useWorldStore((s) => s.bubbles);
  return <>{bubbles.map((b) => <BubbleItem key={b.id} data={b} />)}</>;
}

function BubbleItem({ data }) {
  const ref = useRef();
  const state = useRef({ ...data });
  const removeBubble = useWorldStore((s) => s.removeBubble);
  useFrame((_, delta) => {
    if (!ref.current) return;
    const s = state.current;
    const age = (performance.now() - s.bornAt) / 1000;
    if (age > 4 || s.y > 2) {
      removeBubble(data.id);
      return;
    }
    s.y += s.rise * delta;
    s.x += Math.sin(age * 4 + s.wobble) * delta * 0.6;
    s.z += Math.cos(age * 3 + s.wobble) * delta * 0.6;
    ref.current.position.set(s.x, s.y, s.z);
    const alpha = Math.max(0, 1 - age / 4);
    if (ref.current.material) ref.current.material.opacity = alpha * 0.65;
  });
  return (
    <mesh ref={ref} position={[data.x, data.y, data.z]}>
      <sphereGeometry args={[data.size, 8, 6]} />
      <meshBasicMaterial color="#EAF3FF" transparent opacity={0.65} depthWrite={false} toneMapped={false} />
    </mesh>
  );
}

function UnderwaterWorld() {
  return (
    <>
      <UnderwaterSky />
      <ambientLight intensity={0.5} color="#5A99CC" />
      <directionalLight color="#EAF3FF" intensity={0.9} position={[0, 60, 0]} />
      <hemisphereLight args={["#5A99CC", "#0A2540", 0.6]} />
      <CoralFloor />
      <Trenches />
      <Volcanoes />
      <CrabColony />
      <KelpForest />
      <NamedWrecks />
      <TreasureChests />
      <FishSchool />
      <FishingNets />
      <Whale />
      <Shark />
      <Bubbles />
      {AIRPORTS.map((ap) => (
        <CoralReef key={ap.id} airport={ap} />
      ))}
    </>
  );
}

/* ============================================================
   Underwater terrain — Volcanoes, Trenches, Crabs
   ============================================================ */
function Volcanoes() {
  const list = useMemo(() => ([
    { x: 60, z: -140, scale: 1.4, phase: 0 },
    { x: -220, z: 40, scale: 1.7, phase: 1.7 },
    { x: 200, z: 200, scale: 1.1, phase: 3.1 },
    { x: -60, z: 250, scale: 1.25, phase: 4.4 },
  ]), []);
  return <>{list.map((d, i) => <Volcano key={i} data={d} />)}</>;
}

function Volcano({ data }) {
  const smokeARef = useRef();
  const smokeBRef = useRef();
  const smokeCRef = useRef();
  const glowRef = useRef();
  const lavaRef = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    // Three staggered smoke puffs rising from the crater
    const anim = (ref, offset) => {
      if (!ref.current) return;
      const p = ((t * 0.35 + data.phase + offset) % 1);
      ref.current.position.y = 8.2 + p * 12;
      ref.current.material.opacity = 0.45 * (1 - p);
      ref.current.scale.setScalar(0.9 + p * 3.4);
    };
    anim(smokeARef, 0);
    anim(smokeBRef, 0.33);
    anim(smokeCRef, 0.66);
    if (glowRef.current) glowRef.current.material.emissiveIntensity = 1.3 + Math.sin(t * 3.2 + data.phase) * 0.6;
    if (lavaRef.current) lavaRef.current.material.emissiveIntensity = 0.9 + Math.sin(t * 1.5 + data.phase) * 0.3;
  });
  return (
    <group position={[data.x, -30.4, data.z]} scale={data.scale}>
      {/* Broad basalt base */}
      <mesh position={[0, 0.4, 0]}>
        <cylinderGeometry args={[7.5, 8.5, 0.8, 14]} />
        <meshStandardMaterial color="#1A1410" roughness={1} />
      </mesh>
      {/* Main cone — dark volcanic rock */}
      <mesh position={[0, 4, 0]}>
        <coneGeometry args={[6.2, 8, 12]} />
        <meshStandardMaterial color="#2A1F1C" roughness={1} />
      </mesh>
      {/* Rocky ridges on slope (dodecahedrons) */}
      {[0, 1, 2, 3, 4, 5].map(i => {
        const a = (i / 6) * Math.PI * 2 + data.phase * 0.3;
        const r = 4.2;
        const y = 2 + (i % 2) * 1.5;
        return (
          <mesh key={i} position={[Math.cos(a) * r, y, Math.sin(a) * r]} scale={0.7 + (i % 3) * 0.25}>
            <dodecahedronGeometry args={[0.9, 0]} />
            <meshStandardMaterial color="#3A2820" roughness={1} />
          </mesh>
        );
      })}
      {/* Crater rim */}
      <mesh position={[0, 8.05, 0]}>
        <torusGeometry args={[1.5, 0.35, 8, 14]} />
        <meshStandardMaterial color="#0F0906" roughness={1} />
      </mesh>
      {/* Molten lava pool inside crater */}
      <mesh ref={glowRef} position={[0, 7.9, 0]}>
        <cylinderGeometry args={[1.3, 1.1, 0.4, 14]} />
        <meshStandardMaterial color="#FF6B1A" emissive="#FF4A0A" emissiveIntensity={1.6} toneMapped={false} />
      </mesh>
      {/* Lava spilling down side */}
      <mesh ref={lavaRef} position={[3.1, 4.5, 0.3]} rotation={[0, 0, -1]} scale={[0.35, 3.5, 0.2]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#F0A020" emissive="#FF4A0A" emissiveIntensity={1} toneMapped={false} />
      </mesh>
      {/* Ash/smoke plumes (3 staggered) */}
      <mesh ref={smokeARef} position={[0, 8.2, 0]}>
        <sphereGeometry args={[0.9, 10, 8]} />
        <meshBasicMaterial color="#8B7568" transparent opacity={0.4} depthWrite={false} />
      </mesh>
      <mesh ref={smokeBRef} position={[0.3, 8.2, -0.2]}>
        <sphereGeometry args={[0.8, 10, 8]} />
        <meshBasicMaterial color="#5A4A38" transparent opacity={0.35} depthWrite={false} />
      </mesh>
      <mesh ref={smokeCRef} position={[-0.2, 8.2, 0.3]}>
        <sphereGeometry args={[0.85, 10, 8]} />
        <meshBasicMaterial color="#6B5A48" transparent opacity={0.38} depthWrite={false} />
      </mesh>
      {/* Warm point light from crater */}
      <pointLight color="#FF6B1A" intensity={2.2} distance={28} position={[0, 8, 0]} />
    </group>
  );
}

function Trenches() {
  return (
    <>
      {CANYON_DATA.map((c, i) => <TrenchAmbient key={i} data={c} />)}
      <SeaMounts />
    </>
  );
}

/* Only ambient bio-glow lighting inside the canyons — no decorations */
function TrenchAmbient({ data }) {
  const cx = (data.x1 + data.x2) / 2;
  const cz = (data.z1 + data.z2) / 2;
  const dx = data.x2 - data.x1;
  const dz = data.z2 - data.z1;
  const angle = Math.atan2(dz, dx);
  const length = Math.sqrt(dx * dx + dz * dz);
  return (
    <group position={[cx, -30, cz]} rotation={[0, angle, 0]}>
      <pointLight color="#22D3B4" intensity={0.8} distance={40} position={[length / 4, -data.depth * 0.6, 0]} />
      <pointLight color="#6BB6FF" intensity={0.7} distance={35} position={[-length / 4, -data.depth * 0.7, 0]} />
    </group>
  );
}

/* Summit decorations for each seamount - the mountain body itself is in GeoTerrain */
function SeaMounts() {
  return <>{SEAMOUNT_DATA.map((m, i) => <SeaMountDecor key={i} data={m} />)}</>;
}

function SeaMountDecor({ data }) {
  const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
  return (
    <group position={[data.x, -30 + data.height, data.z]}>
      {/* Coral colonies on the summit */}
      {[...Array(8)].map((_, i) => {
        const a = (i / 8) * Math.PI * 2 + data.seed;
        const r = 1 + rng(i * 3 + data.seed) * 3;
        const y = -1 - rng(i * 5) * 3;
        const colors = ["#E94B4B", "#F0A020", "#8B5CF6", "#22D3B4", "#5A99CC", "#B01A2E", "#F4C4A0", "#22C55E"];
        return (
          <mesh
            key={`c-${i}`}
            position={[Math.cos(a) * r, y, Math.sin(a) * r]}
          >
            <coneGeometry args={[0.55, 1.8, 7]} />
            <meshStandardMaterial color={colors[i]} emissive={colors[i]} emissiveIntensity={0.45} roughness={0.7} />
          </mesh>
        );
      })}
      {/* Tube worms / anemones on the summit */}
      {[...Array(6)].map((_, i) => {
        const a = (i / 6) * Math.PI * 2 + data.seed * 1.3;
        const r = 0.5 + rng(i * 7) * 1.8;
        return (
          <mesh key={`an-${i}`} position={[Math.cos(a) * r, -0.2, Math.sin(a) * r]}>
            <cylinderGeometry args={[0.14, 0.22, 1.4, 6]} />
            <meshStandardMaterial color="#F0A020" emissive="#F0A020" emissiveIntensity={0.7} toneMapped={false} />
          </mesh>
        );
      })}
      {/* A few scattered boulders on the top */}
      {[...Array(4)].map((_, i) => {
        const a = (i / 4) * Math.PI * 2 + data.seed * 0.5;
        return (
          <mesh
            key={`b-${i}`}
            position={[Math.cos(a) * 2.5, -0.5, Math.sin(a) * 2.5]}
            rotation={[rng(i * 3) * Math.PI, rng(i * 5) * Math.PI, rng(i * 7) * Math.PI]}
            scale={0.7 + rng(i * 11) * 0.6}
          >
            <dodecahedronGeometry args={[1, 0]} />
            <meshStandardMaterial color="#3A2A1E" roughness={1} />
          </mesh>
        );
      })}
      {/* Warm summit light */}
      <pointLight color="#F0A020" intensity={1.1} distance={25} position={[0, 2, 0]} />
    </group>
  );
}
function CrabColony() {
  const crabs = useMemo(() => {
    const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
    const colors = ["#B01A2E", "#E94B4B", "#F0A020", "#8B4A2E", "#C9682E"];
    const arr = [];
    for (let i = 0; i < 8; i++) {
      const a = rng(i * 3.3) * Math.PI * 2;
      const r = 20 + rng(i * 5.9) * 200;
      arr.push({
        id: i,
        x: Math.cos(a) * r,
        z: Math.sin(a) * r,
        radius: 1.5 + rng(i * 2.1) * 2.5,
        speed: 0.4 + rng(i * 7.7) * 0.5,
        phase: rng(i * 11) * Math.PI * 2,
        scale: 0.9 + rng(i * 13) * 0.8,
        color: colors[i % colors.length],
      });
    }
    return arr;
  }, []);
  return <>{crabs.map((c) => <Crab key={c.id} data={c} />)}</>;
}

function Crab({ data }) {
  const ref = useRef();
  const legRefA = useRef();
  const legRefB = useRef();
  const clawL = useRef();
  const clawR = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime + data.phase;
    // Walk in a small circular path along the seafloor
    const wa = t * data.speed * 0.3;
    ref.current.position.x = data.x + Math.cos(wa) * data.radius;
    ref.current.position.z = data.z + Math.sin(wa) * data.radius;
    // Crabs walk sideways — face perpendicular to motion
    ref.current.rotation.y = -wa;
    // Legs shuffle
    if (legRefA.current) legRefA.current.rotation.z = Math.sin(t * 9) * 0.25;
    if (legRefB.current) legRefB.current.rotation.z = -Math.sin(t * 9) * 0.25;
    // Claws pinch
    if (clawL.current) clawL.current.rotation.y = Math.sin(t * 4) * 0.35;
    if (clawR.current) clawR.current.rotation.y = -Math.sin(t * 4) * 0.35;
  });
  return (
    <group ref={ref} position={[data.x, -29.9, data.z]} scale={data.scale}>
      {/* Domed carapace (shell) */}
      <mesh position={[0, 0.25, 0]} scale={[1.5, 0.55, 1.1]}>
        <sphereGeometry args={[0.5, 14, 10]} />
        <meshStandardMaterial color={data.color} roughness={0.55} metalness={0.15} />
      </mesh>
      {/* Shell dark spots */}
      <mesh position={[0.2, 0.5, 0.15]}>
        <sphereGeometry args={[0.08, 6, 6]} />
        <meshStandardMaterial color="#3A1810" roughness={0.9} />
      </mesh>
      <mesh position={[-0.15, 0.5, -0.18]}>
        <sphereGeometry args={[0.08, 6, 6]} />
        <meshStandardMaterial color="#3A1810" roughness={0.9} />
      </mesh>
      <mesh position={[0.05, 0.55, 0.25]}>
        <sphereGeometry args={[0.06, 6, 6]} />
        <meshStandardMaterial color="#3A1810" roughness={0.9} />
      </mesh>
      {/* Eyes on stalks */}
      <mesh position={[0.55, 0.55, 0.18]}>
        <cylinderGeometry args={[0.03, 0.03, 0.18, 6]} />
        <meshStandardMaterial color={data.color} />
      </mesh>
      <mesh position={[0.55, 0.7, 0.18]}>
        <sphereGeometry args={[0.07, 8, 6]} />
        <meshStandardMaterial color="#0B0A0C" />
      </mesh>
      <mesh position={[0.55, 0.55, -0.18]}>
        <cylinderGeometry args={[0.03, 0.03, 0.18, 6]} />
        <meshStandardMaterial color={data.color} />
      </mesh>
      <mesh position={[0.55, 0.7, -0.18]}>
        <sphereGeometry args={[0.07, 8, 6]} />
        <meshStandardMaterial color="#0B0A0C" />
      </mesh>
      {/* Left front claw */}
      <group ref={clawL} position={[0.5, 0.2, 0.55]} rotation={[0, -0.5, 0]}>
        <mesh position={[0.35, 0, 0]}>
          <boxGeometry args={[0.7, 0.14, 0.14]} />
          <meshStandardMaterial color={data.color} roughness={0.55} />
        </mesh>
        <mesh position={[0.75, 0, 0]} scale={[1, 1.5, 1.6]}>
          <sphereGeometry args={[0.22, 10, 8]} />
          <meshStandardMaterial color={data.color} roughness={0.55} />
        </mesh>
        <mesh position={[0.95, 0.1, 0]} scale={[1.3, 0.3, 0.6]}>
          <sphereGeometry args={[0.18, 8, 6]} />
          <meshStandardMaterial color={data.color} roughness={0.55} />
        </mesh>
      </group>
      {/* Right front claw */}
      <group ref={clawR} position={[0.5, 0.2, -0.55]} rotation={[0, 0.5, 0]}>
        <mesh position={[0.35, 0, 0]}>
          <boxGeometry args={[0.7, 0.14, 0.14]} />
          <meshStandardMaterial color={data.color} roughness={0.55} />
        </mesh>
        <mesh position={[0.75, 0, 0]} scale={[1, 1.5, 1.6]}>
          <sphereGeometry args={[0.22, 10, 8]} />
          <meshStandardMaterial color={data.color} roughness={0.55} />
        </mesh>
        <mesh position={[0.95, 0.1, 0]} scale={[1.3, 0.3, 0.6]}>
          <sphereGeometry args={[0.18, 8, 6]} />
          <meshStandardMaterial color={data.color} roughness={0.55} />
        </mesh>
      </group>
      {/* 3 pairs of walking legs (side A) */}
      <group ref={legRefA}>
        {[-0.25, 0, 0.28].map((xo, i) => (
          <group key={`la-${i}`} position={[xo, 0.15, 0.5]} rotation={[0, 0, 0]}>
            <mesh position={[0.15, 0, 0.15]} rotation={[0.5, 0.4, -0.4]}>
              <boxGeometry args={[0.42, 0.08, 0.08]} />
              <meshStandardMaterial color={data.color} roughness={0.55} />
            </mesh>
            <mesh position={[0.28, -0.15, 0.32]} rotation={[0.3, 0.6, 0.2]}>
              <boxGeometry args={[0.35, 0.07, 0.07]} />
              <meshStandardMaterial color={data.color} roughness={0.55} />
            </mesh>
          </group>
        ))}
      </group>
      {/* 3 pairs of walking legs (side B) */}
      <group ref={legRefB}>
        {[-0.25, 0, 0.28].map((xo, i) => (
          <group key={`lb-${i}`} position={[xo, 0.15, -0.5]}>
            <mesh position={[0.15, 0, -0.15]} rotation={[-0.5, -0.4, -0.4]}>
              <boxGeometry args={[0.42, 0.08, 0.08]} />
              <meshStandardMaterial color={data.color} roughness={0.55} />
            </mesh>
            <mesh position={[0.28, -0.15, -0.32]} rotation={[-0.3, -0.6, 0.2]}>
              <boxGeometry args={[0.35, 0.07, 0.07]} />
              <meshStandardMaterial color={data.color} roughness={0.55} />
            </mesh>
          </group>
        ))}
      </group>
    </group>
  );
}

/* Coral reef marker for each section - underwater equivalent of Airport */
function CoralReef({ airport }) {
  const nearestId = useWorldStore((s) => s.nearestId);
  const active = nearestId === airport.id;
  const groupRef = useRef();
  const glowRef = useRef();
  const [x, , z] = airport.position;

  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    groupRef.current.rotation.y = t * 0.08;
    if (glowRef.current) glowRef.current.material.emissiveIntensity = 1.3 + Math.sin(t * 2) * 0.6;
  });

  return (
    <group position={[x, -28, z]}>
      {/* Wide sand plateau */}
      <mesh position={[0, 0.1, 0]}>
        <cylinderGeometry args={[10, 12, 0.4, 24]} />
        <meshStandardMaterial color="#C9A776" roughness={0.95} />
      </mesh>
      {/* Rock mound base */}
      <mesh position={[0, 1.5, 0]} scale={[3.5, 1.6, 3.5]}>
        <sphereGeometry args={[2, 16, 12]} />
        <meshStandardMaterial color="#5A4A48" roughness={0.95} />
      </mesh>
      {/* Colored coral ring (12 corals in circle) */}
      <group ref={groupRef}>
        {[...Array(12)].map((_, i) => {
          const a = (i / 12) * Math.PI * 2;
          const r = 3.2;
          const h = 5 + (i % 3) * 1.8;
          return (
            <mesh key={i} position={[Math.cos(a) * r, 2 + h / 2, Math.sin(a) * r]}>
              <coneGeometry args={[0.7, h, 6]} />
              <meshStandardMaterial color={airport.color} emissive={airport.color} emissiveIntensity={active ? 0.9 : 0.5} roughness={0.75} />
            </mesh>
          );
        })}
      </group>
      {/* Central TALL obelisk pillar in category color (very visible from any angle) */}
      <mesh position={[0, 12, 0]}>
        <cylinderGeometry args={[0.6, 1.2, 18, 6]} />
        <meshStandardMaterial color={airport.color} emissive={airport.color} emissiveIntensity={active ? 1.4 : 0.85} toneMapped={false} />
      </mesh>
      {/* Ring bands around pillar */}
      {[6, 10, 14, 18].map((y, i) => (
        <mesh key={`b-${i}`} position={[0, y, 0]}>
          <torusGeometry args={[1.1, 0.15, 8, 16]} />
          <meshStandardMaterial color="#F4EADE" emissive={airport.color} emissiveIntensity={0.6} metalness={0.8} roughness={0.15} />
        </mesh>
      ))}
      {/* Top beacon orb - bright and pulsating */}
      <mesh ref={glowRef} position={[0, 22, 0]}>
        <sphereGeometry args={[1.3, 20, 16]} />
        <meshStandardMaterial color={airport.color} emissive={airport.color} emissiveIntensity={1.6} toneMapped={false} />
      </mesh>
      {/* Radiating light beams shooting UP from the beacon */}
      <mesh position={[0, 30, 0]}>
        <cylinderGeometry args={[0.15, 0.8, 15, 8, 1, true]} />
        <meshBasicMaterial color={airport.color} transparent opacity={0.35} depthWrite={false} toneMapped={false} />
      </mesh>
      {/* Floating label - HIGH so visible from any depth */}
      <Billboard position={[0, 30, 0]} follow>
        <Text
          fontSize={5}
          color={airport.color}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.12}
          outlineColor="#020814"
          letterSpacing={0.05}
        >
          {airport.label}
        </Text>
      </Billboard>
      <Billboard position={[0, 26, 0]} follow>
        <Text
          fontSize={1.1}
          color="#EAF3FF"
          anchorX="center"
          anchorY="middle"
          letterSpacing={0.32}
        >
          {`> ${airport.code} · REEF SECTOR`}
        </Text>
      </Billboard>
      {active && (
        <Html center position={[0, 20, 0]} zIndexRange={[10, 0]} distanceFactor={22}>
          <div className="pointer-events-none select-none whitespace-nowrap px-4 py-2 rounded-md bg-[rgba(10,53,96,0.85)] border border-[rgba(107,182,255,0.6)] text-[#F4EADE] font-mono text-xs uppercase tracking-[0.24em] shadow-[0_0_40px_rgba(107,182,255,0.5)]">
            <span className="text-[#6BB6FF]">[E]</span> Explore · {airport.label}
          </div>
        </Html>
      )}
      <pointLight color={airport.color} intensity={active ? 3.5 : 2.2} distance={80} position={[0, 15, 0]} />
    </group>
  );
}

/* Named historical shipwrecks - Titanic + Andrea Doria + Bismarck + USS Arizona */
function NamedWrecks() {
  return (
    <>
      <NamedWreck
        position={[-100, -28, 100]}
        rotation={[0.08, 1.2, -0.15]}
        name="RMS TITANIC"
        year="1912"
        length={36}
        type="titanic"
      />
      <NamedWreck
        position={[130, -28, 90]}
        rotation={[0.05, -0.4, -1.1]}
        name="ANDREA DORIA"
        year="1956"
        length={26}
        type="doria"
      />
      <NamedWreck
        position={[-180, -28, -180]}
        rotation={[0.12, 2.2, -0.3]}
        name="BISMARCK"
        year="1941"
        length={30}
        type="bismarck"
      />
      <NamedWreck
        position={[70, -28, -50]}
        rotation={[0.05, 0.6, -0.25]}
        name="USS ARIZONA"
        year="1941"
        length={32}
        type="arizona"
      />
    </>
  );
}

function NamedWreck({ position, rotation, name, year, length, type }) {
  /* Ocean liner (Titanic): black hull, red boot-top, 4 iconic yellow-black funnels,
     hull broken in two like the real wreck. */
  const renderTitanic = () => {
    const hullBackLen = length * 0.55;
    const hullFrontLen = length * 0.38;
    return (
      <>
        {/* Rear hull section (stern with propellers - lies flatter) */}
        <group position={[-length / 4, 0, 0]}>
          <mesh position={[0, 1.6, 0]} scale={[hullBackLen / 2, 1.6, 2.4]}>
            <sphereGeometry args={[1, 20, 12]} />
            <meshStandardMaterial color="#0A0A0C" roughness={0.95} />
          </mesh>
          {/* Red boot-top */}
          <mesh position={[0, 0.6, 0]} scale={[hullBackLen / 2, 0.35, 2.5]}>
            <sphereGeometry args={[1, 20, 8]} />
            <meshStandardMaterial color="#8B1A1A" roughness={0.9} />
          </mesh>
          {/* Rear deck */}
          <mesh position={[0, 3.2, 0]}>
            <boxGeometry args={[hullBackLen * 0.85, 0.25, 4]} />
            <meshStandardMaterial color="#3A2A20" roughness={1} />
          </mesh>
          {/* Two propellers at very stern */}
          {[-1.2, 1.2].map((zo, i) => (
            <group key={i} position={[-hullBackLen / 2 + 0.3, 1.1, zo]}>
              <mesh rotation={[0, 0, Math.PI / 2]}>
                <cylinderGeometry args={[0.25, 0.25, 0.4, 8]} />
                <meshStandardMaterial color="#8B6A2E" metalness={0.85} roughness={0.4} />
              </mesh>
              {[0, 1, 2].map(b => (
                <mesh key={b} position={[-0.2, 0, 0]} rotation={[(b * Math.PI * 2) / 3, 0, 0]}>
                  <boxGeometry args={[0.1, 1.3, 0.35]} />
                  <meshStandardMaterial color="#8B6A2E" metalness={0.85} roughness={0.4} />
                </mesh>
              ))}
            </group>
          ))}
          {/* Rudder */}
          <mesh position={[-hullBackLen / 2 + 0.1, 1.6, 0]}>
            <boxGeometry args={[0.4, 1.6, 0.2]} />
            <meshStandardMaterial color="#1A1410" roughness={0.95} />
          </mesh>
          {/* Superstructure (white cabin decks) */}
          <mesh position={[0.5, 4.2, 0]}>
            <boxGeometry args={[hullBackLen * 0.55, 1.4, 3.4]} />
            <meshStandardMaterial color="#D8D0C4" roughness={0.85} />
          </mesh>
          {/* Two aft funnels (yellow with black top) - tilted from age */}
          {[-1.2, 1.5].map((xo, i) => (
            <group key={i} position={[xo, 6.4, 0]} rotation={[0, 0, -0.15 - i * 0.05]}>
              <mesh position={[0, 0, 0]}>
                <cylinderGeometry args={[0.8, 0.9, 3.8, 14]} />
                <meshStandardMaterial color="#C9A34E" roughness={0.85} />
              </mesh>
              {/* Black top band */}
              <mesh position={[0, 1.7, 0]}>
                <cylinderGeometry args={[0.82, 0.82, 0.6, 14]} />
                <meshStandardMaterial color="#0A0A0C" roughness={0.9} />
              </mesh>
              {/* Support cables */}
              <mesh position={[-0.5, -0.7, 1.0]} rotation={[0, 0, 0.35]}>
                <cylinderGeometry args={[0.03, 0.03, 2, 4]} />
                <meshStandardMaterial color="#3A2A20" />
              </mesh>
            </group>
          ))}
        </group>
        {/* Front hull section (bow, split, further away and tilted forward) */}
        <group position={[length / 4 + 2, -1, 0.6]} rotation={[0, 0.15, -0.35]}>
          <mesh position={[0, 1.4, 0]} scale={[hullFrontLen / 2, 1.5, 2.3]}>
            <sphereGeometry args={[1, 20, 12]} />
            <meshStandardMaterial color="#0A0A0C" roughness={0.95} />
          </mesh>
          {/* Red boot-top on bow */}
          <mesh position={[0, 0.5, 0]} scale={[hullFrontLen / 2, 0.3, 2.4]}>
            <sphereGeometry args={[1, 20, 8]} />
            <meshStandardMaterial color="#8B1A1A" roughness={0.9} />
          </mesh>
          {/* Bow deck */}
          <mesh position={[0, 2.85, 0]}>
            <boxGeometry args={[hullFrontLen * 0.9, 0.2, 3.8]} />
            <meshStandardMaterial color="#3A2A20" roughness={1} />
          </mesh>
          {/* Bow forward funnels (2) - broken/tilted */}
          {[0.3, 2.5].map((xo, i) => (
            <group key={i} position={[xo, 5.5, 0]} rotation={[0, 0, -0.25 - i * 0.1]}>
              <mesh>
                <cylinderGeometry args={[0.75, 0.85, 3.5, 14]} />
                <meshStandardMaterial color="#C9A34E" roughness={0.85} />
              </mesh>
              <mesh position={[0, 1.5, 0]}>
                <cylinderGeometry args={[0.77, 0.77, 0.55, 14]} />
                <meshStandardMaterial color="#0A0A0C" roughness={0.9} />
              </mesh>
            </group>
          ))}
          {/* Distinctive bow stem going up sharply */}
          <mesh position={[hullFrontLen / 2 - 0.3, 2.2, 0]} rotation={[0, 0, 0.35]}>
            <boxGeometry args={[2, 2.2, 2]} />
            <meshStandardMaterial color="#0A0A0C" roughness={0.95} />
          </mesh>
          {/* Anchor chain hole */}
          <mesh position={[hullFrontLen / 2 - 0.1, 1.6, 0.9]}>
            <sphereGeometry args={[0.18, 8, 6]} />
            <meshStandardMaterial color="#1A1410" />
          </mesh>
        </group>
        {/* Debris field between the two halves */}
        {[[-1.5, 0.3, 1.2], [1, 0.3, -1.5], [3, 0.3, 0.5], [-3, 0.3, -0.5]].map((p, i) => (
          <mesh key={`d-${i}`} position={p} scale={0.6 + (i % 3) * 0.2}>
            <dodecahedronGeometry args={[0.4, 0]} />
            <meshStandardMaterial color="#1A1410" roughness={1} />
          </mesh>
        ))}
      </>
    );
  };

  /* Andrea Doria: passenger liner known for capsizing on its side. Single funnel. */
  const renderDoria = () => (
    <>
      {/* Hull (single piece, black with white superstructure) */}
      <mesh position={[0, 1.5, 0]} scale={[length / 2, 1.4, 2.2]}>
        <sphereGeometry args={[1, 20, 12]} />
        <meshStandardMaterial color="#0F1520" roughness={0.9} />
      </mesh>
      {/* Red boot-top */}
      <mesh position={[0, 0.5, 0]} scale={[length / 2, 0.3, 2.3]}>
        <sphereGeometry args={[1, 20, 8]} />
        <meshStandardMaterial color="#8B1A1A" roughness={0.9} />
      </mesh>
      {/* White superstructure decks (multi-level) */}
      <mesh position={[0, 3, 0]}>
        <boxGeometry args={[length * 0.7, 1.2, 3.2]} />
        <meshStandardMaterial color="#EFE3D6" roughness={0.85} />
      </mesh>
      <mesh position={[0, 4.1, 0]}>
        <boxGeometry args={[length * 0.55, 0.9, 2.6]} />
        <meshStandardMaterial color="#EFE3D6" roughness={0.85} />
      </mesh>
      {/* Row of dark portholes/windows */}
      <mesh position={[0, 3, 1.61]}>
        <boxGeometry args={[length * 0.65, 0.35, 0.02]} />
        <meshStandardMaterial color="#0F1520" emissive="#5A99CC" emissiveIntensity={0.2} />
      </mesh>
      {/* Iconic single fat funnel (green-white-red Italian tricolor stripes) */}
      <group position={[-length / 8, 6, 0]}>
        <mesh>
          <cylinderGeometry args={[1.1, 1.3, 3, 16]} />
          <meshStandardMaterial color="#EFE3D6" roughness={0.85} />
        </mesh>
        {/* Italia colored bands */}
        <mesh position={[0, 1, 0]}>
          <cylinderGeometry args={[1.12, 1.12, 0.35, 16]} />
          <meshStandardMaterial color="#2E8B57" />
        </mesh>
        <mesh position={[0, 0.6, 0]}>
          <cylinderGeometry args={[1.12, 1.12, 0.35, 16]} />
          <meshStandardMaterial color="#B01A2E" />
        </mesh>
        {/* Top black cap */}
        <mesh position={[0, 1.5, 0]}>
          <cylinderGeometry args={[1.14, 1.14, 0.35, 16]} />
          <meshStandardMaterial color="#0A0A0C" />
        </mesh>
      </group>
      {/* Bridge */}
      <mesh position={[length / 3.5, 4.6, 0]}>
        <boxGeometry args={[3, 1.2, 2.4]} />
        <meshStandardMaterial color="#EFE3D6" roughness={0.85} />
      </mesh>
      {/* Lifeboats along the deck (visible on sunken ship) */}
      {[-4, -2, 0, 2, 4].map((xo, i) => (
        <mesh key={i} position={[xo, 3.9, 1.55]} scale={[1, 0.35, 0.5]}>
          <sphereGeometry args={[0.5, 8, 6]} />
          <meshStandardMaterial color="#F0A020" roughness={0.9} />
        </mesh>
      ))}
      {/* Propeller */}
      <group position={[-length / 2 + 0.5, 1.2, 0]}>
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.25, 0.25, 0.4, 8]} />
          <meshStandardMaterial color="#8B6A2E" metalness={0.85} roughness={0.4} />
        </mesh>
        {[0, 1, 2, 3].map(b => (
          <mesh key={b} position={[-0.2, 0, 0]} rotation={[(b * Math.PI * 2) / 4, 0, 0]}>
            <boxGeometry args={[0.1, 1.4, 0.4]} />
            <meshStandardMaterial color="#8B6A2E" metalness={0.85} roughness={0.4} />
          </mesh>
        ))}
      </group>
    </>
  );

  /* Bismarck: WWII battleship - flat wide hull, main gun turrets, low profile */
  const renderBismarck = () => (
    <>
      {/* Wide armored hull (grey) */}
      <mesh position={[0, 1.4, 0]} scale={[length / 2, 1.3, 2.6]}>
        <sphereGeometry args={[1, 20, 12]} />
        <meshStandardMaterial color="#3A4048" roughness={0.9} metalness={0.3} />
      </mesh>
      {/* Camouflage dark band */}
      <mesh position={[0, 1.2, 0]} scale={[length / 2 + 0.02, 0.25, 2.62]}>
        <sphereGeometry args={[1, 20, 8]} />
        <meshStandardMaterial color="#1A2530" roughness={0.9} />
      </mesh>
      {/* Wooden deck */}
      <mesh position={[0, 2.55, 0]}>
        <boxGeometry args={[length * 0.9, 0.15, 4.4]} />
        <meshStandardMaterial color="#5A4030" roughness={1} />
      </mesh>
      {/* Fore main turret A (twin barrels) */}
      <group position={[length * 0.32, 3.1, 0]}>
        <mesh>
          <cylinderGeometry args={[0.85, 1, 0.7, 12]} />
          <meshStandardMaterial color="#4A5058" roughness={0.85} metalness={0.35} />
        </mesh>
        <mesh position={[1.2, 0.1, 0.3]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.12, 0.12, 2.6, 8]} />
          <meshStandardMaterial color="#2A3040" roughness={0.85} metalness={0.4} />
        </mesh>
        <mesh position={[1.2, 0.1, -0.3]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.12, 0.12, 2.6, 8]} />
          <meshStandardMaterial color="#2A3040" roughness={0.85} metalness={0.4} />
        </mesh>
      </group>
      {/* Fore main turret B (elevated superfiring) */}
      <group position={[length * 0.15, 3.5, 0]}>
        <mesh position={[0, -0.2, 0]}>
          <cylinderGeometry args={[0.95, 1.05, 0.4, 12]} />
          <meshStandardMaterial color="#3A4048" roughness={0.9} />
        </mesh>
        <mesh>
          <cylinderGeometry args={[0.85, 1, 0.7, 12]} />
          <meshStandardMaterial color="#4A5058" roughness={0.85} metalness={0.35} />
        </mesh>
        <mesh position={[1.2, 0.1, 0.3]} rotation={[0, 0, Math.PI / 2 + 0.1]}>
          <cylinderGeometry args={[0.12, 0.12, 2.6, 8]} />
          <meshStandardMaterial color="#2A3040" metalness={0.4} />
        </mesh>
        <mesh position={[1.2, 0.1, -0.3]} rotation={[0, 0, Math.PI / 2 + 0.1]}>
          <cylinderGeometry args={[0.12, 0.12, 2.6, 8]} />
          <meshStandardMaterial color="#2A3040" metalness={0.4} />
        </mesh>
      </group>
      {/* Aft main turret C */}
      <group position={[-length * 0.28, 3.1, 0]} rotation={[0, Math.PI, 0]}>
        <mesh>
          <cylinderGeometry args={[0.85, 1, 0.7, 12]} />
          <meshStandardMaterial color="#4A5058" roughness={0.85} metalness={0.35} />
        </mesh>
        <mesh position={[1.2, 0.1, 0.3]} rotation={[0, 0, Math.PI / 2 - 0.15]}>
          <cylinderGeometry args={[0.12, 0.12, 2.6, 8]} />
          <meshStandardMaterial color="#2A3040" metalness={0.4} />
        </mesh>
        <mesh position={[1.2, 0.1, -0.3]} rotation={[0, 0, Math.PI / 2 - 0.15]}>
          <cylinderGeometry args={[0.12, 0.12, 2.6, 8]} />
          <meshStandardMaterial color="#2A3040" metalness={0.4} />
        </mesh>
      </group>
      {/* Aft turret D (elevated) */}
      <group position={[-length * 0.12, 3.5, 0]} rotation={[0, Math.PI, 0]}>
        <mesh position={[0, -0.2, 0]}>
          <cylinderGeometry args={[0.95, 1.05, 0.4, 12]} />
          <meshStandardMaterial color="#3A4048" roughness={0.9} />
        </mesh>
        <mesh>
          <cylinderGeometry args={[0.85, 1, 0.7, 12]} />
          <meshStandardMaterial color="#4A5058" roughness={0.85} metalness={0.35} />
        </mesh>
        <mesh position={[1.2, 0.1, 0.3]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.12, 0.12, 2.6, 8]} />
          <meshStandardMaterial color="#2A3040" metalness={0.4} />
        </mesh>
        <mesh position={[1.2, 0.1, -0.3]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.12, 0.12, 2.6, 8]} />
          <meshStandardMaterial color="#2A3040" metalness={0.4} />
        </mesh>
      </group>
      {/* Central superstructure / bridge tower */}
      <mesh position={[0.5, 3.7, 0]}>
        <boxGeometry args={[4, 1.6, 2.6]} />
        <meshStandardMaterial color="#3A4048" roughness={0.85} />
      </mesh>
      {/* Command tower (tall, iconic Bismarck spotting top) */}
      <mesh position={[1, 5.2, 0]}>
        <cylinderGeometry args={[0.55, 0.7, 1.6, 10]} />
        <meshStandardMaterial color="#4A5058" roughness={0.85} />
      </mesh>
      <mesh position={[1, 6.3, 0]}>
        <sphereGeometry args={[0.75, 12, 10]} />
        <meshStandardMaterial color="#3A4048" roughness={0.85} />
      </mesh>
      {/* Radar/mast */}
      <mesh position={[1, 7.5, 0]}>
        <cylinderGeometry args={[0.05, 0.08, 1.6, 6]} />
        <meshStandardMaterial color="#5A5D62" metalness={0.6} />
      </mesh>
      {/* Single funnel */}
      <group position={[-length * 0.05, 4.6, 0]}>
        <mesh>
          <cylinderGeometry args={[0.65, 0.75, 1.8, 14]} />
          <meshStandardMaterial color="#3A4048" roughness={0.85} />
        </mesh>
        <mesh position={[0, 0.95, 0]}>
          <cylinderGeometry args={[0.67, 0.67, 0.25, 14]} />
          <meshStandardMaterial color="#0A0A0C" roughness={0.9} />
        </mesh>
      </group>
      {/* Secondary AA guns along sides */}
      {[-2, 0, 2].map((xo, i) => (
        <group key={i} position={[xo, 3, 2.3]}>
          <mesh>
            <cylinderGeometry args={[0.28, 0.3, 0.35, 8]} />
            <meshStandardMaterial color="#4A5058" />
          </mesh>
          <mesh position={[0.5, 0.15, 0]} rotation={[0, 0, Math.PI / 2 - 0.3]}>
            <cylinderGeometry args={[0.05, 0.05, 1, 6]} />
            <meshStandardMaterial color="#2A3040" />
          </mesh>
        </group>
      ))}
    </>
  );

  /* USS Arizona: WWII US battleship - upside-down / broken hull with iconic capsized bow */
  const renderArizona = () => (
    <>
      {/* Broken hull - main midsection resting flat (grey warship) */}
      <mesh position={[0, 1.2, 0]} scale={[length / 2, 1.1, 2.5]}>
        <sphereGeometry args={[1, 20, 12]} />
        <meshStandardMaterial color="#4A4E52" roughness={0.9} metalness={0.35} />
      </mesh>
      {/* Broken bow section (tilted) */}
      <mesh position={[length * 0.4, 1.6, 0.5]} rotation={[0, 0.2, 0.55]} scale={[length / 4, 1, 2.2]}>
        <sphereGeometry args={[1, 20, 12]} />
        <meshStandardMaterial color="#4A4E52" roughness={0.9} metalness={0.35} />
      </mesh>
      {/* Blast crater / gaping hole in midsection (dark void) */}
      <mesh position={[0, 2.4, 0]} scale={[3, 0.6, 2]}>
        <sphereGeometry args={[1, 12, 10]} />
        <meshStandardMaterial color="#0A0806" roughness={1} />
      </mesh>
      {/* Charred edges of the blast (fire orange embers) */}
      <mesh position={[0, 2.5, 0]} scale={[3.3, 0.5, 2.2]}>
        <torusGeometry args={[1, 0.15, 6, 16]} />
        <meshStandardMaterial color="#3A1810" emissive="#B03015" emissiveIntensity={0.7} toneMapped={false} />
      </mesh>
      {/* Wooden teak deck */}
      <mesh position={[0, 2.4, 0]}>
        <boxGeometry args={[length * 0.85, 0.12, 4]} />
        <meshStandardMaterial color="#5A4030" roughness={1} />
      </mesh>
      {/* Fallen tripod mast (iconic Arizona feature) - tilted over on side */}
      <group position={[length * 0.1, 3, 1.5]} rotation={[0, 0, -0.7]}>
        {/* Three tripod legs */}
        <mesh position={[0, 3, 0]}>
          <cylinderGeometry args={[0.15, 0.2, 6, 6]} />
          <meshStandardMaterial color="#3A4048" roughness={0.85} />
        </mesh>
        <mesh position={[0.3, 3, 0.5]} rotation={[0.15, 0, 0]}>
          <cylinderGeometry args={[0.12, 0.16, 6.2, 6]} />
          <meshStandardMaterial color="#3A4048" roughness={0.85} />
        </mesh>
        <mesh position={[0.3, 3, -0.5]} rotation={[-0.15, 0, 0]}>
          <cylinderGeometry args={[0.12, 0.16, 6.2, 6]} />
          <meshStandardMaterial color="#3A4048" roughness={0.85} />
        </mesh>
        {/* Spotting top platform */}
        <mesh position={[0, 6.3, 0]}>
          <cylinderGeometry args={[0.6, 0.7, 0.7, 10]} />
          <meshStandardMaterial color="#4A5058" roughness={0.85} />
        </mesh>
      </group>
      {/* Two big turrets remaining */}
      {[length * 0.28, -length * 0.28].map((xo, i) => (
        <group key={i} position={[xo, 2.85, 0]} rotation={[0, i * Math.PI, 0]}>
          <mesh>
            <cylinderGeometry args={[0.9, 1.05, 0.65, 12]} />
            <meshStandardMaterial color="#4A5058" roughness={0.85} metalness={0.35} />
          </mesh>
          {/* Three barrels (Arizona had triple-gun turrets) */}
          {[-0.3, 0, 0.3].map((zo, j) => (
            <mesh key={j} position={[1.1, 0.05, zo]} rotation={[0, 0, Math.PI / 2 - 0.05]}>
              <cylinderGeometry args={[0.11, 0.11, 2.4, 8]} />
              <meshStandardMaterial color="#2A3040" metalness={0.4} />
            </mesh>
          ))}
        </group>
      ))}
      {/* Central superstructure */}
      <mesh position={[-length * 0.1, 3.4, 0]}>
        <boxGeometry args={[4.5, 1.4, 2.6]} />
        <meshStandardMaterial color="#4A4E52" roughness={0.85} />
      </mesh>
      {/* Oil slick still leaking (dark iridescent patch above wreck) */}
      <mesh position={[0, 6, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[length * 0.9, 5]} />
        <meshStandardMaterial color="#0F0806" transparent opacity={0.75} depthWrite={false} emissive="#3A1810" emissiveIntensity={0.15} side={THREE.DoubleSide} />
      </mesh>
      {/* Rising oil droplets (subtle) */}
      {[-length * 0.3, -length * 0.1, length * 0.15, length * 0.3].map((xo, i) => (
        <mesh key={`oil-${i}`} position={[xo, 4 + i * 0.5, i % 2 === 0 ? 1 : -1]}>
          <sphereGeometry args={[0.18, 8, 6]} />
          <meshStandardMaterial color="#1A1108" roughness={0.5} transparent opacity={0.7} />
        </mesh>
      ))}
      {/* American flag on submerged flagpole (partly torn) */}
      <group position={[-length * 0.35, 3.8, 0]} rotation={[0, 0, -0.35]}>
        <mesh position={[0, 1.5, 0]}>
          <cylinderGeometry args={[0.07, 0.07, 3.5, 6]} />
          <meshStandardMaterial color="#B9AFA6" metalness={0.8} />
        </mesh>
        <mesh position={[1.2, 2.6, 0]}>
          <planeGeometry args={[2.2, 1.4]} />
          <meshStandardMaterial color="#8B1A1A" side={THREE.DoubleSide} roughness={0.95} transparent opacity={0.85} />
        </mesh>
        <mesh position={[0.6, 3, 0.01]}>
          <planeGeometry args={[1, 0.6]} />
          <meshStandardMaterial color="#2A3B6A" side={THREE.DoubleSide} roughness={0.95} transparent opacity={0.85} />
        </mesh>
      </group>
      {/* Anti-aircraft guns (Arizona had many) */}
      {[-2, 2].map((xo, i) => (
        <group key={`aa-${i}`} position={[xo, 3.2, 1.8]}>
          <mesh>
            <cylinderGeometry args={[0.22, 0.25, 0.3, 8]} />
            <meshStandardMaterial color="#4A5058" />
          </mesh>
          <mesh position={[0, 0.3, 0]} rotation={[0.4, 0, 0]}>
            <cylinderGeometry args={[0.05, 0.05, 0.9, 6]} />
            <meshStandardMaterial color="#2A3040" />
          </mesh>
        </group>
      ))}
    </>
  );

  return (
    <group position={position} rotation={rotation}>
      {type === "titanic" && renderTitanic()}
      {type === "doria" && renderDoria()}
      {type === "bismarck" && renderBismarck()}
      {type === "arizona" && renderArizona()}
      {/* Coral & rust growth on wreck (accents) */}
      <mesh position={[-length / 6, 3.2, 1.7]}>
        <coneGeometry args={[0.5, 1.4, 6]} />
        <meshStandardMaterial color="#E94B4B" emissive="#E94B4B" emissiveIntensity={0.35} />
      </mesh>
      <mesh position={[length / 6, 3, -1.7]}>
        <coneGeometry args={[0.55, 1.5, 6]} />
        <meshStandardMaterial color="#8B5CF6" emissive="#8B5CF6" emissiveIntensity={0.35} />
      </mesh>
      <mesh position={[-length / 3, 3.4, 0.8]}>
        <coneGeometry args={[0.4, 1, 6]} />
        <meshStandardMaterial color="#22D3B4" emissive="#22D3B4" emissiveIntensity={0.3} />
      </mesh>
      <mesh position={[length / 8, 3.3, 2]}>
        <sphereGeometry args={[0.35, 8, 6]} />
        <meshStandardMaterial color="#F0A020" emissive="#F0A020" emissiveIntensity={0.25} />
      </mesh>
      {/* Rust patches (dark irregular blobs on hull) */}
      <mesh position={[length / 5, 1.8, 2.4]} scale={[0.8, 0.3, 0.05]}>
        <sphereGeometry args={[1, 8, 6]} />
        <meshStandardMaterial color="#5A2018" roughness={1} />
      </mesh>
      <mesh position={[-length / 5, 1.6, -2.4]} scale={[0.6, 0.4, 0.05]}>
        <sphereGeometry args={[1, 8, 6]} />
        <meshStandardMaterial color="#5A2018" roughness={1} />
      </mesh>
      {/* Label floating above with year */}
      <Billboard position={[0, 13, 0]} follow>
        <Text
          fontSize={2.6}
          color="#EAF3FF"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.06}
          outlineColor="#020814"
          letterSpacing={0.06}
        >
          {name}
        </Text>
      </Billboard>
      <Billboard position={[0, 10.5, 0]} follow>
        <Text
          fontSize={0.9}
          color="#F0A020"
          anchorX="center"
          anchorY="middle"
          letterSpacing={0.32}
        >
          {`SUNK ${year}`}
        </Text>
      </Billboard>
      <pointLight color="#5A99CC" intensity={0.6} distance={22} position={[0, 5, 0]} />
    </group>
  );
}

/* Kelp forest - tall wavy seaweed groups scattered across the floor */
function KelpForest() {
  const clumps = useMemo(() => {
    const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
    const arr = [];
    for (let i = 0; i < 18; i++) {
      const a = rng(i * 3.1) * Math.PI * 2;
      const r = 35 + rng(i * 5.7) * 140;
      arr.push({
        x: Math.cos(a) * r,
        z: Math.sin(a) * r,
        seed: i * 1.9,
        stalks: 4 + Math.floor(rng(i * 7) * 5),
      });
    }
    return arr;
  }, []);
  return <>{clumps.map((c, i) => <KelpClump key={i} data={c} />)}</>;
}

function KelpClump({ data }) {
  const refs = useRef([]);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    refs.current.forEach((r, i) => {
      if (r) r.rotation.z = Math.sin(t * 0.8 + i + data.seed) * 0.15;
    });
  });
  const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
  return (
    <group position={[data.x, -30, data.z]}>
      {[...Array(data.stalks)].map((_, i) => {
        const ox = (rng(data.seed + i * 3) - 0.5) * 3;
        const oz = (rng(data.seed + i * 5) - 0.5) * 3;
        const h = 6 + rng(data.seed + i * 7) * 6;
        return (
          <group key={i} position={[ox, 0, oz]} ref={(el) => (refs.current[i] = el)}>
            <mesh position={[0, h / 2, 0]}>
              <cylinderGeometry args={[0.08, 0.12, h, 5]} />
              <meshStandardMaterial color="#2A6B45" roughness={0.9} />
            </mesh>
            {/* Leaves (small planes offset along stalk) */}
            {[...Array(4)].map((_, j) => (
              <mesh key={j} position={[0.3, (j + 1) * (h / 5), 0]} rotation={[0, 0, 0.3]}>
                <planeGeometry args={[1.6, 0.4]} />
                <meshStandardMaterial color="#3A8B55" side={THREE.DoubleSide} roughness={0.85} transparent opacity={0.9} />
              </mesh>
            ))}
          </group>
        );
      })}
    </group>
  );
}

/* Sunken ship - one wrecked cargo lying on seafloor */
function SunkenShip() {
  return (
    <group position={[70, -29, -50]} rotation={[0.15, 0.6, -0.35]}>
      {/* Hull (rotated onto side) */}
      <mesh position={[0, 1.5, 0]}>
        <boxGeometry args={[18, 3, 4]} />
        <meshStandardMaterial color="#3A2A24" roughness={0.95} />
      </mesh>
      {/* Broken deck (partial) */}
      <mesh position={[-3, 3.2, 0]}>
        <boxGeometry args={[8, 0.5, 3.8]} />
        <meshStandardMaterial color="#251E18" roughness={1} />
      </mesh>
      {/* Cabin (broken) */}
      <mesh position={[-6.5, 4.5, 0]}>
        <boxGeometry args={[2.8, 2, 3]} />
        <meshStandardMaterial color="#4A3A2E" roughness={0.9} />
      </mesh>
      {/* Broken mast lying */}
      <mesh position={[3, 4.5, 0]} rotation={[0, 0, -0.4]}>
        <cylinderGeometry args={[0.2, 0.2, 8, 6]} />
        <meshStandardMaterial color="#2A2018" />
      </mesh>
      {/* Coral growing on wreck */}
      <mesh position={[-2, 3.6, 0.5]}>
        <coneGeometry args={[0.6, 1.6, 6]} />
        <meshStandardMaterial color="#E94B4B" emissive="#E94B4B" emissiveIntensity={0.3} />
      </mesh>
      <mesh position={[2, 3.6, -1]}>
        <coneGeometry args={[0.5, 1.4, 6]} />
        <meshStandardMaterial color="#8B5CF6" emissive="#8B5CF6" emissiveIntensity={0.3} />
      </mesh>
    </group>
  );
}

/* Treasure chests scattered - closed with glowing seam */
function TreasureChests() {
  const chests = useMemo(() => {
    const rng = (s) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
    // helper: min distance from point to any canyon center-line
    const distToCanyon = (px, pz) => {
      let best = Infinity;
      for (const c of CANYON_DATA) {
        const vx = c.x2 - c.x1;
        const vz = c.z2 - c.z1;
        const lenSq = vx * vx + vz * vz;
        const t = Math.max(0, Math.min(1, ((px - c.x1) * vx + (pz - c.z1) * vz) / lenSq));
        const nx = c.x1 + t * vx;
        const nz = c.z1 + t * vz;
        const d = Math.sqrt((px - nx) * (px - nx) + (pz - nz) * (pz - nz)) - c.width;
        if (d < best) best = d;
      }
      return best;
    };
    const arr = [];
    let attempts = 0;
    while (arr.length < 5 && attempts < 40) {
      const i = attempts;
      const a = rng(i * 3.3 + 1) * Math.PI * 2;
      const r = 30 + rng(i * 5.7 + 2) * 140;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      attempts++;
      if (distToCanyon(x, z) < 8) continue; // keep at least 8m clear of any canyon edge
      arr.push({ x, z, rot: rng(i * 7 + 3) * Math.PI * 2, seed: i });
    }
    return arr;
  }, []);
  const glowRef = useRef([]);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    glowRef.current.forEach((r, i) => {
      if (r && r.material) r.material.emissiveIntensity = 1 + Math.sin(t * 2 + i) * 0.5;
    });
  });
  return (
    <>
      {chests.map((c, i) => (
        <group key={i} position={[c.x, -29.7, c.z]} rotation={[0, c.rot, 0]}>
          {/* Chest base (wooden box) */}
          <mesh position={[0, 0.8, 0]}>
            <boxGeometry args={[2.4, 1.6, 1.6]} />
            <meshStandardMaterial color="#6B3B1F" roughness={0.9} />
          </mesh>
          {/* Wood plank grooves - horizontal darker lines */}
          {[0.4, 0.8, 1.2].map((yo, j) => (
            <mesh key={`p-${j}`} position={[0, yo, 0.81]}>
              <boxGeometry args={[2.42, 0.06, 0.02]} />
              <meshStandardMaterial color="#3A2010" />
            </mesh>
          ))}
          {/* Domed lid (half cylinder rotated) */}
          <mesh position={[0, 1.7, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.85, 0.85, 2.4, 12, 1, false, 0, Math.PI]} />
            <meshStandardMaterial color="#8B4A28" roughness={0.85} />
          </mesh>
          {/* Metal bands (front, middle, back) wrapping around chest */}
          {[-0.9, 0, 0.9].map((zo, j) => (
            <mesh key={`band-${j}`} position={[0, 1.1, zo]}>
              <boxGeometry args={[2.5, 0.15, 0.15]} />
              <meshStandardMaterial color="#3A3A3A" metalness={0.8} roughness={0.3} />
            </mesh>
          ))}
          {/* Vertical band on lid over each */}
          {[-0.9, 0, 0.9].map((zo, j) => (
            <mesh key={`vb-${j}`} position={[0, 1.85, zo]} rotation={[0, 0, Math.PI / 2]}>
              <torusGeometry args={[0.87, 0.06, 6, 8, Math.PI]} />
              <meshStandardMaterial color="#3A3A3A" metalness={0.8} roughness={0.3} />
            </mesh>
          ))}
          {/* Big brass padlock on the front */}
          <mesh position={[0, 1.2, 0.85]}>
            <boxGeometry args={[0.45, 0.55, 0.18]} />
            <meshStandardMaterial color="#F0A020" metalness={0.9} roughness={0.15} emissive="#F0A020" emissiveIntensity={0.35} />
          </mesh>
          {/* Padlock shackle (semicircle) */}
          <mesh position={[0, 1.55, 0.85]} rotation={[0, 0, 0]}>
            <torusGeometry args={[0.18, 0.05, 6, 10, Math.PI]} />
            <meshStandardMaterial color="#B08830" metalness={0.9} roughness={0.15} />
          </mesh>
          {/* Keyhole */}
          <mesh position={[0, 1.15, 0.95]} ref={(el) => (glowRef.current[i] = el)}>
            <circleGeometry args={[0.08, 8]} />
            <meshStandardMaterial color="#0B0A0C" emissive="#F0A020" emissiveIntensity={1.5} toneMapped={false} side={THREE.DoubleSide} />
          </mesh>
          {/* Corner rivets */}
          {[
            [-1.15, 0.15, 0.75], [1.15, 0.15, 0.75], [-1.15, 0.15, -0.75], [1.15, 0.15, -0.75],
            [-1.15, 1.25, 0.75], [1.15, 1.25, 0.75], [-1.15, 1.25, -0.75], [1.15, 1.25, -0.75],
          ].map((p, j) => (
            <mesh key={`r-${j}`} position={p}>
              <sphereGeometry args={[0.08, 6, 6]} />
              <meshStandardMaterial color="#3A3A3A" metalness={0.85} roughness={0.25} />
            </mesh>
          ))}
          {/* Gold coins spilling from side */}
          {[[0.8, 0.15, 1.05], [1.1, 0.15, 0.85], [0.6, 0.15, 1.2], [1.3, 0.15, 1.0], [0.4, 0.15, 1.1]].map((p, j) => (
            <mesh key={`coin-${j}`} position={p} rotation={[Math.PI / 2, 0, j * 0.5]}>
              <cylinderGeometry args={[0.14, 0.14, 0.04, 10]} />
              <meshStandardMaterial color="#F0A020" metalness={0.9} roughness={0.1} emissive="#F0A020" emissiveIntensity={0.4} />
            </mesh>
          ))}
          <pointLight color="#F0A020" intensity={1.1} distance={10} position={[0, 1.5, 0]} />
        </group>
      ))}
    </>
  );
}

export default function Scene() {
  const spaceMode = useWorldStore((s) => s.spaceMode);
  const underwaterMode = useWorldStore((s) => s.underwaterMode);
  // Generate the procedural planet / Earth textures during idle time so the
  // Earth -> Space transition doesn't hitch on first entry.
  useEffect(() => {
    prewarmSpaceTextures(AIRPORTS);
  }, []);
  return (
    <Canvas
      shadows={false}
      dpr={[1, 1.6]}
      camera={{ position: [0, 26, 70], fov: 55, near: 0.1, far: 1200 }}
      gl={{ antialias: true, powerPreference: "high-performance" }}
    >
      <Suspense fallback={null}>
        {spaceMode ? <SpaceWorld /> : (underwaterMode ? <UnderwaterWorld /> : <EarthWorld />)}
        <Parcels />
        <Airplane />
      </Suspense>
    </Canvas>
  );
}
